import { AppSelect } from './components/ui/AppSelect';
import React, { lazy, Suspense, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Routes, Route, useNavigate, useParams, useLocation, Navigate, Outlet, useMatch
} from "react-router-dom";
import {
  Users, Image as ImageIcon, Clapperboard,
  PanelLeftClose, PanelLeftOpen, Snowflake, Box, History,
  Rotate3D, ChevronDown, ChevronRight,
  ArrowLeft, Folder, LayoutGrid, LogOut, Film, Layers, Palette, Check, BookOpen, Workflow, X,
  MessageSquare, Scissors, MoreHorizontal, ScrollText, FileText, Bot, LayoutDashboard, Music, Swords, Video, Languages,
  Gamepad2, TrendingUp
} from "lucide-react";

// 组件引入
import CoolUploader from "./components/CoolUploader";
import AssetConfirmModal from "./components/AssetConfirmModal";
import StoryboardTable from "./components/storyboard/StoryboardTable";
import CharacterGenerator from "./components/CharacterGenerator";
import SceneGenerator from "./components/SceneGenerator";
import PropGenerator from "./components/PropGenerator";
import SnowOverlay from "./components/SnowOverlay";
import HistoryGallery from "./components/HistoryGallery";
import ResourceCenter from "./components/resource/ResourceCenter";
import ImageWorkstation from "./components/ImageWorkstation";
import Orbit3DPage from "./components/Orbit3DPage";
import ProjectDashboard from "./components/dashboard/ProjectDashboard";
import EpisodeSelector from "./components/dashboard/EpisodeSelector";
import AdminDashboard from "./components/admin/AdminDashboard";
import VideoAgentModern from "./components/video-agent/VideoAgentModern";
import ImageAgentModern from "./components/image-agent/ImageAgentModern";
import MusicAgentModern from "./components/music-agent/MusicAgentModern";
import FightActionWorkshop from "./components/FightActionWorkshop";
import EducationCenter from "./components/education/EducationCenter";
import MotionStoryboardPage from "./components/MotionStoryboardPage";
import CanvasMode from "./components/canvas-mode/CanvasMode";
import AdminMonitor from "./pages/AdminMonitor";
import UserActivityPage from "./pages/UserActivityPage";
import AssetManagePage from "./pages/AssetManagePage";
import HelpCenter from "./pages/HelpCenter";
import LoginPage from "./pages/LoginPage";
import ProviderProbePage from "./pages/ProviderProbePage";
import ReferenceVideoReversePage from "./pages/ReferenceVideoReversePage";
import AiOwnershipAgreementPage from "./pages/AiOwnershipAgreementPage";
import ProjectOutlinePage from "./components/outline/ProjectOutlinePage";
import CostStatisticsPage from "./components/dashboard/CostStatisticsPage";
import RoughCutPage from "./components/rough-cut/RoughCutPage";
import ScriptAnalysisPage from "./components/script-analysis/ScriptAnalysisPage";
import GalleryPage from "./components/gallery/GalleryPage";
import AIWriterPage from "./components/script-writer/AIWriterPage";
import AdaptationAgentWorkbench from "./components/adaptation-agent/AdaptationAgentWorkbench";
import EpisodeProductionWorkbench from "./components/adaptation-agent/EpisodeProductionWorkbench";
import EpisodeStoryboardBoardWorkbench from "./components/adaptation-agent/EpisodeStoryboardBoardWorkbench";
import MiniStoryboardWorkbench from "./components/adaptation-agent/MiniStoryboardWorkbench";
import EpisodeReviewFeedbackPage from "./components/adaptation-agent/EpisodeReviewFeedbackPage";
import StoryStructureWorkbench from "./components/story-structure/StoryStructureWorkbench";
import MasterShotStoryboardPage from "./components/master-shot/MasterShotStoryboardPage";
import AIActingGuidePage from "./components/acting-guide/AIActingGuidePage";
import ProjectOnboardingGate from "./components/onboarding/ProjectOnboarding";
import AiChatPanel from "./components/ai-chat/AiChatPanel";
import GenerationLogPanel from "./components/generation-log/GenerationLogPanel";
import GlobalScriptViewer from "./components/common/GlobalScriptViewer";
import ProjectQuickAccessDock from "./components/common/ProjectQuickAccessDock";
const CreationGuidePanel = lazy(() => import("./components/creation-guide/CreationGuidePanel"));
const HongguoRankingsPage = lazy(() => import("./pages/HongguoRankingsPage"));
const OperationsPage = lazy(() => import("./pages/OperationsPage"));
const AnnualRecapPage = lazy(() => import("./pages/AnnualRecapPage"));
import UserPointBalance from "./components/common/UserPointBalance";
import ProjectTypeSettingsModal from "./components/project/ProjectTypeSettingsModal";
import PixelOfficePage from "./components/pixel-office/PixelOfficePage";
import PixelProjectRoom from "./components/pixel-office/PixelProjectRoom";
import PixelEpisodeRoom from "./components/pixel-office/PixelEpisodeRoom";
import NarrationProjectWorkbench from "./components/narration-project/NarrationProjectWorkbench";
import VideoRepaintWorkbench from "./components/video-repaint/VideoRepaintWorkbench";
import InteractiveStoryWorkbench from "./components/interactive-story/InteractiveStoryWorkbench";
import useProjectPermissions from "./hooks/useProjectPermissions";
import ProjectAccessBoundary from './components/common/ProjectAccessBoundary';
import useDelayedUnmount from "./hooks/useDelayedUnmount";

import { useUser } from "./context/UserContext";
import { getLoginLandingPath } from './utils/userRoles';
import { useToast } from "./context/ToastContext";
import { useTheme } from "./context/ThemeContext";
import { useStyle } from "./context/StyleContext";
import { useI18n } from "./context/I18nContext";
import { authenticatedFetch } from "./utils/api";
import { API_HOST } from "./utils/constants";
import { submitTask, startTaskPolling } from "./services/taskService";
import { fetchPublicVisualReferences } from "./services/visualReferenceService";
import { attachVisualStyleReferences, normalizeVisualStyleKey } from "./utils/visualStyleReferences";
import {
  getProjectEntryPath,
  isInteractiveGameProject,
  isNarrationProject,
  isRepaintProject,
  isSd25Project,
  isWan3Project,
  getVideoProviderLabel,
  normalizeProjectVideoRatio,
  rememberProjectProductionMode,
  PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME,
  PROJECT_PRODUCTION_MODE_NARRATION,
  PROJECT_PRODUCTION_MODE_REPAINT,
  PROJECT_VIDEO_RATIOS,
  VIDEO_PROVIDER_OMINI_SEEDANCE,
  VIDEO_PROVIDER_WANX,
} from "./utils/projectRuntime";
import {
  PROJECT_TYPE_OPTIONS,
  buildProjectTypeUpdatePayload,
} from "./utils/projectTypeSettings";
import { STYLE_OPTIONS } from "./components/storyboard/common/Constants";
import { MODEL_MENU_PROVIDERS, normalizeModelMenuProvider } from "./constants/globalAiProviders";
import { isLegacyAstraProvider } from "./constants/astraflowAstraModels";
import { setStoredImageProvider } from "./utils/wanxImageCapabilities";
import {
  DREAMFLY_LLM_MODEL_STORAGE_KEY,
  DREAMFLY_LLM_PROVIDER_ID,
  isDreamflyImageApiModel,
} from "./constants/dreamflyModels";
import { fetchDreamflyLlmModels } from "./services/dreamflyModelService";

const WORKSPACE_TAB_LABELS = {
  upload: "本集剧本",
  storyboard: "分镜编辑器",
  "master-shot": "主镜参考",
  "storyboard-board": "故事板导演台",
  "mini-storyboard": "小故事板",
  canvas: "画布模式",
  "rough-cut": "粗剪台",
  imageagent: "图片生成",
  videoagent: "视频生成",
  musicagent: "音乐音频",
  character: "角色工坊",
  scene: "场景工坊",
  prop: "道具工坊",
  education: "教学中心",
  resources: "资源中心",
  "motion-storyboard": "动态分镜",
  orbit: "3D 视角",
  "ai-writer": "智能改编",
  "ai-writer-classic": "传统写作",
};

const isOverseasProject = (project) => (
  project?.projectMarket === "overseas"
  || project?.contentLanguage === "en-US"
  || project?.videoProvider === "engguavideo"
  || project?.videoProvider === VIDEO_PROVIDER_OMINI_SEEDANCE
);

// CoolUploader 已有专属文案的阶段（scriptwriter/storyboarder/director/costume_designer/finalizing）
const KNOWN_LOADING_STAGES = new Set(['scriptwriter', 'storyboarder', 'director', 'costume_designer', 'finalizing']);

// 将后端任务阶段映射为 CoolUploader 的 loadingStage。
// 未识别的阶段不再回退成 'scriptwriter'，而是以 `stage:` 前缀透传后端原文
// （优先 currentStageText 中文文案），由 CoolUploader 直接展示，避免 UI 长时间停在同一句话。
const resolveLoadingStageFromProgress = (progressData, fallbackStage) => {
  const currentStage = progressData?.currentStage;
  const currentStageText = progressData?.currentStageText;
  if (currentStage === 'completed') return 'finalizing';
  if (KNOWN_LOADING_STAGES.has(currentStage)) return currentStage;
  const rawText = currentStageText || currentStage;
  if (rawText) return `stage:${rawText}`;
  return fallbackStage;
};

// 侧边栏分组抽屉（模块级组件；原先在渲染期用 useCallback 创建，会导致子组件每次渲染重置）
const NavDrawer = ({ id, title, color, children, className = "", open, onToggle }) => (
  <div className={className}>
    <button
      type="button"
      onClick={() => onToggle(id)}
      className="mb-2 flex h-9 w-full items-center justify-between rounded-xl px-3 text-left transition-colors cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
      style={{ color: 'var(--color-text-secondary)' }}
    >
      <span className="inline-flex min-w-0 items-center gap-2 text-xs font-bold tracking-wider">
        <span className="h-4 w-0.5 rounded-full" style={{ backgroundColor: color }} />
        <span className="truncate">{title}</span>
      </span>
      <ChevronRight size={14} className={`shrink-0 transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
    </button>
    {open && <div className="space-y-1">{children}</div>}
  </div>
);

const projectMarketMeta = (project) => {
  if (isInteractiveGameProject(project)) {
    return { label: "互动游戏", provider: "专用工作流", color: "var(--color-accent-primary)", colorBorder: "color-mix(in srgb, var(--color-accent-primary) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-primary) 7%, transparent)" };
  }
  if (isNarrationProject(project)) {
    return { label: "解说剧", provider: getVideoProviderLabel(project?.videoProvider), color: "var(--color-accent-primary)", colorBorder: "color-mix(in srgb, var(--color-accent-primary) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-primary) 7%, transparent)" };
  }
  if (isRepaintProject(project)) {
    return { label: "影视转绘", provider: getVideoProviderLabel(project?.videoProvider), color: "var(--color-success)", colorBorder: "color-mix(in srgb, var(--color-success) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-success) 7%, transparent)" };
  }
  if (isWan3Project(project) || project?.videoProvider === VIDEO_PROVIDER_WANX) {
    return { label: "Wan3.0 项目", provider: "万相 3.0", color: "var(--color-accent-primary)", colorBorder: "color-mix(in srgb, var(--color-accent-primary) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-primary) 7%, transparent)" };
  }
  if (isSd25Project(project)) {
    return { label: "2.5 项目", provider: getVideoProviderLabel(project?.videoProvider), color: "var(--color-accent-primary)", colorBorder: "color-mix(in srgb, var(--color-accent-primary) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-primary) 7%, transparent)" };
  }
  if (project?.videoProvider === VIDEO_PROVIDER_OMINI_SEEDANCE) {
    return { label: "出海项目", provider: "GuaOmini", color: "var(--color-info, #38bdf8)", colorBorder: "color-mix(in srgb, var(--color-info, #38bdf8) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-info, #38bdf8) 7%, transparent)" };
  }
  return isOverseasProject(project)
    ? { label: "出海项目", provider: "EngGuaVideo", color: "var(--color-success)", colorBorder: "color-mix(in srgb, var(--color-success) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-success) 7%, transparent)" }
    : { label: "国内项目", provider: "GuaVideo", color: "var(--color-accent-secondary)", colorBorder: "color-mix(in srgb, var(--color-accent-secondary) 33%, transparent)", colorSoft: "color-mix(in srgb, var(--color-accent-secondary) 7%, transparent)" };
};

const isNoFixedVisualStyle = (value) => {
  const key = String(value || "").trim().toLowerCase();
  return key === "custom" || key === "none" || key === "no_style";
};

const getProjectVisualStyleMode = (project) => {
  const mode = String(project?.visualStyleMode || "").trim().toLowerCase();
  if (mode === "fixed" && project?.visualStyle) return "fixed";
  if (mode === "none") return "none";
  return "legacy";
};

const getProjectVisualStyleValue = (project, fallback = "modern_cdrama") => {
  const mode = getProjectVisualStyleMode(project);
  if (mode === "fixed") return project.visualStyle;
  if (mode === "none") return "custom";
  return fallback || "modern_cdrama";
};

const PROJECT_VIEW_LABELS = {
  overview: "项目总览",
  outline: "剧情大纲",
  script: "剧本中心",
  structure: "故事骨架",
  adaptation: "智能改编",
  novel: "小说转剧本",
  production: "对话短剧自动流",
  narration: "解说剧生产",
  repaint: "影视转绘",
  cost: "费用统计",
};

const PROJECT_ASSET_LABELS = {
  character: "角色工坊",
  scene: "场景工坊",
  prop: "道具工坊",
  fight: "招式工坊",
  image: "图片资源",
  resources: "资源中心",
};

const ENCODED_OCTET_RE = /%[0-9A-Fa-f]{2}/;

const decodePossiblyEncodedText = (value) => {
  if (value == null) return "";
  let text = String(value);
  for (let i = 0; i < 3 && ENCODED_OCTET_RE.test(text); i += 1) {
    try {
      const next = decodeURIComponent(text);
      if (next === text) break;
      text = next;
    } catch {
      break;
    }
  }
  return text;
};

const displayUserId = (value, fallback = "未登录") => {
  const text = decodePossiblyEncodedText(value).trim();
  return text || fallback;
};

const parseJsonIfPossible = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const unwrapTaskResult = (value, depth = 0) => {
  if (depth > 8 || value == null) return value;
  const parsed = parseJsonIfPossible(value);
  if (parsed !== value) return unwrapTaskResult(parsed, depth + 1);
  if (Array.isArray(parsed)) return parsed;
  if (typeof parsed !== "object") return parsed;

  if (parsed.storyboard !== undefined || parsed.extractedAssets !== undefined || parsed.assets !== undefined) {
    return {
      ...parsed,
      storyboard: parsed.storyboard !== undefined ? unwrapTaskResult(parsed.storyboard, depth + 1) : parsed.storyboard,
    };
  }

  for (const key of ["result", "value", "data", "resultPayload"]) {
    if (parsed[key] !== undefined && parsed[key] !== null) {
      return unwrapTaskResult(parsed[key], depth + 1);
    }
  }

  return parsed;
};

const normalizeTaskResultObject = (result) => {
  const unwrapped = unwrapTaskResult(result);
  if (unwrapped && typeof unwrapped === "object" && !Array.isArray(unwrapped)) return unwrapped;
  return { storyboard: unwrapped };
};

const extractStoryboardRows = (result) => {
  const unwrapped = unwrapTaskResult(result);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (unwrapped && typeof unwrapped === "object") {
    const candidate = unwrapped.storyboard ?? unwrapped.shots ?? unwrapped.items ?? unwrapped.rows;
    const rows = unwrapTaskResult(candidate);
    if (Array.isArray(rows)) return rows;
  }
  return null;
};

const normalizeEpisodeLabel = (episode, fallbackId) => {
  if (!episode) return fallbackId ? `第${fallbackId}集` : "未选择集数";
  const order = episode.sortOrder || episode.episodeNumber || episode.number || fallbackId;
  const rawTitle = episode.title || episode.name || (order ? `第${order}集` : "未命名");
  if (!order) return rawTitle;
  const cleanTitle = String(rawTitle)
    .replace(new RegExp(`^第\\s*${order}\\s*集\\s*[：:·\\-—_\\s]*`), "")
    .replace(/^第\s*\d+\s*集\s*[：:·\-—_\s]*/, "")
    .trim();
  return cleanTitle ? `第${order}集 ${cleanTitle}` : `第${order}集`;
};

const DreamflyLlmModelSelect = ({
  value,
  options = [],
  loading = false,
  error = "",
  onChange,
  compact = false,
}) => {
  const hasSelectedOption = options.some((option) => option.id === value);
  return (
    <AppSelect
      value={value || ""}
      onChange={(event) => onChange?.(event.target.value)}
      disabled={!value && (loading || options.length === 0)}
      className={`${compact ? "h-8 max-w-[220px] px-2 text-[11px]" : "mt-1.5 h-9 w-full px-2.5 text-xs"} rounded-md border outline-none`}
      style={{
        borderColor: "var(--color-border-primary)",
        backgroundColor: "var(--color-bg-secondary)",
        color: "var(--color-text-secondary)",
      }}
      title={error || "Dreamfly LLM 子模型"}
      aria-label="Dreamfly LLM 子模型"
    >
      <option value="" disabled>
        {loading ? "正在加载模型目录…" : (error ? "模型目录加载失败" : "请选择模型")}
      </option>
      {value && !hasSelectedOption && <option value={value}>{value}</option>}
      {options.map((option) => (
        <option key={option.id} value={option.id}>{option.label || option.id}</option>
      ))}
    </AppSelect>
  );
};

const formatDreamflyLlmDisplayName = (modelId, modelOption) => {
  const modelLabel = String(modelOption?.label || modelId || "").trim();
  if (!modelLabel) return "Dreamfly · 未选择模型";
  return /^dreamfly(?:\s|·|-)/i.test(modelLabel)
    ? modelLabel
    : `Dreamfly · ${modelLabel}`;
};

/**
 * ✅ 1. 主程序入口
 * 定义路由结构，使用 MainLayout 作为父级
 */
function App() {
  const { initialized, token, isAdmin, canAccessOperations } = useUser();
  const location = useLocation();
  const isOwnershipAgreementPath = location.pathname.replace(/\/+$/, '') === '/ai-ownership-agreement';

  // 权属说明必须可以作为公开材料直接访问，不依赖登录接口是否可用。
  if (isOwnershipAgreementPath) {
    return <AiOwnershipAgreementPage />;
  }

  if (!initialized) {
    return (
      <div className="w-full min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)' }}>
        正在检查登录状态...
      </div>
    );
  }

  if (!token) {
    return <LoginPage />;
  }

  return (
    <div className="w-full min-h-screen font-sans overflow-hidden" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
      <Routes>
        <Route path="/annual-recap" element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center" role="status" style={{ background: '#101015', color: '#eee8dd' }}>正在打开你的年度创作回响…</div>}>
            <AnnualRecapPage />
          </Suspense>
        } />
        <Route path="/operations" element={canAccessOperations ? (
          <Suspense fallback={<div className="p-8" role="status" style={{ color: 'var(--color-text-muted)' }}>正在打开运营工作台…</div>}>
            <OperationsPage />
          </Suspense>
        ) : <Navigate to="/" replace />} />
        {/* 管理后台 - 独立路由，不包含主侧边栏 */}
        <Route path="/admin" element={isAdmin ? <AdminDashboard /> : <Navigate to="/" replace />} />
        <Route path="/admin/users" element={isAdmin ? <AdminDashboard initialTab="users" /> : <Navigate to="/" replace />} />
        <Route path="/admin/prompts" element={isAdmin ? <AdminDashboard initialTab="prompts" /> : <Navigate to="/" replace />} />
        <Route path="/admin/secrets" element={isAdmin ? <AdminDashboard initialTab="secrets" /> : <Navigate to="/" replace />} />
        <Route path="/admin/visual-references" element={isAdmin ? <AdminDashboard initialTab="visualRefs" /> : <Navigate to="/" replace />} />
        <Route path="/admin/monitor" element={isAdmin ? <AdminMonitor /> : <Navigate to="/" replace />} />
        <Route path="/admin/activity" element={isAdmin ? <UserActivityPage /> : <Navigate to="/" replace />} />
        <Route path="/admin/assets" element={isAdmin ? <AssetManagePage /> : <Navigate to="/" replace />} />
        <Route path="/admin/provider-probe" element={isAdmin ? <ProviderProbePage /> : <Navigate to="/" replace />} />
        <Route path="/provider-probe" element={<Navigate to={isAdmin ? "/admin/provider-probe" : "/"} replace />} />
        <Route path="/reference-video-reverse" element={<ReferenceVideoReversePage />} />

        {/* 所有页面都包裹在 MainLayout 中，保证 Sidebar 永远存在 */}
        <Route element={<ProjectAccessBoundary><MainLayout /></ProjectAccessBoundary>}>
          <Route path="/" element={<HomeModeSwitch />} />
          <Route path="/project/:projectId" element={<EpisodeWrapper />} />
          <Route path="/project/:projectId/narration-production" element={<NarrationProjectWorkbench />} />
          <Route path="/project/:projectId/video-repaint" element={<VideoRepaintWorkbench />} />
          <Route path="/project/:projectId/interactive-story" element={<InteractiveStoryWorkbench />} />
          <Route path="/project/:projectId/office" element={<PixelProjectRoom />} />
          <Route path="/project/:projectId/canvas" element={<ProjectCanvasWrapper />} />
          <Route path="/project/:projectId/outline" element={<ProjectOutlinePage />} />
          <Route path="/project/:projectId/cost" element={<CostStatisticsPage />} />
          <Route path="/project/:projectId/script-analysis" element={<ScriptAnalysisPage />} />
          <Route path="/project/:projectId/story-structure" element={<StoryStructureWrapper />} />
          <Route path="/project/:projectId/acting-guide" element={<AIActingGuidePage />} />
          <Route path="/project/:projectId/reference-video-reverse" element={<ReferenceVideoReversePage />} />
          <Route path="/project/:projectId/one-click-explainer" element={<NovelAgentWrapper autoMode="explainer" />} />
          <Route path="/project/:projectId/dialogue-auto" element={<NovelAgentWrapper autoMode="dialogue" />} />
          <Route path="/project/:projectId/novel-agent" element={<NovelAgentWrapper />} />
          <Route path="/project/:projectId/adaptation-agent/episodes" element={<EpisodeProductionWrapper />} />
          <Route path="/project/:projectId/adaptation-agent" element={<AdaptationAgentWrapper />} />
          <Route path="/project/:projectId/music" element={<ProjectMusicWrapper />} />
          <Route path="/project/:projectId/episode/:episodeId/rough-cut" element={<RoughCutPage />} />
          <Route path="/project/:projectId/episode/:episodeId/review-feedback" element={<EpisodeReviewFeedbackPage />} />
          <Route path="/project/:projectId/episode/:episodeId/office" element={<PixelEpisodeRoom />} />
          {/* 项目级别资产管理路由 — 统一入口，切换不卸载 */}
          <Route path="/project/:projectId/assets/:assetType" element={<ProjectAssetsWrapper />} />
          {/* AI 创作（必须在 :tab? 之前，否则会被通配匹配） */}
          <Route path="/project/:projectId/episode/:episodeId/ai-writer" element={<AdaptationAgentWrapper />} />
          <Route path="/project/:projectId/episode/:episodeId/ai-writer-classic" element={<AIWriterPage />} />
          {/* 工作台路由：:tab 是可选参数 */}
          <Route path="/project/:projectId/episode/:episodeId/:tab?" element={<WorkspaceContent />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/hongguo-rankings" element={
            <Suspense fallback={<div className="p-8 pl-20" role="status" style={{ color: "var(--color-text-muted)" }}>正在打开红果榜单…</div>}>
              <HongguoRankingsPage />
            </Suspense>
          } />
          <Route path="/help" element={<HelpCenter />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

/**
 * ✅ 2. 全局布局组件 (核心修复点)
 * 包含 Sidebar 和 右侧内容区(Outlet)
 */
const MainLayout = () => {
  const { userId, loginWithPassword, logout, isAdmin, canAccessOperations } = useUser();
  const displayUser = displayUserId(userId);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentTheme, switchTheme, getTheme, themes } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const { languageLabel, toggleLanguage, t } = useI18n();

  // 侧边栏状态
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // 下雪特效默认关闭，localStorage 记住用户选择
  const [isSnowing, setIsSnowing] = useState(() => localStorage.getItem("snow_effect") === "1");
  const toggleSnow = useCallback(() => {
    setIsSnowing(prev => {
      const next = !prev;
      localStorage.setItem("snow_effect", next ? "1" : "0");
      return next;
    });
  }, []);
  const [currentProvider, setCurrentProvider] = useState(() => normalizeModelMenuProvider(localStorage.getItem("ai_provider")));
  const [dreamflyLlmModel, setDreamflyLlmModel] = useState(() => {
    const storedModel = String(localStorage.getItem(DREAMFLY_LLM_MODEL_STORAGE_KEY) || "").trim();
    return isDreamflyImageApiModel(storedModel) ? "" : storedModel;
  });
  const [dreamflyLlmCatalog, setDreamflyLlmCatalog] = useState({
    models: [],
    loading: currentProvider === DREAMFLY_LLM_PROVIDER_ID,
    error: "",
  });
  const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
  const [isThemeMenuOpen, setIsThemeMenuOpen] = useState(false);
  const [isSwitchUserOpen, setIsSwitchUserOpen] = useState(false);
  const [isProjectTypeSettingsOpen, setIsProjectTypeSettingsOpen] = useState(false);
  const sidebarBackdropVisibility = useDelayedUnmount(isSidebarOpen, 140);
  const switchUserVisibility = useDelayedUnmount(isSwitchUserOpen, 140);
  const [switchUserInput, setSwitchUserInput] = useState("");
  const [adminPasswordInput, setAdminPasswordInput] = useState("");
  const [needPassword, setNeedPassword] = useState(true);
  const [passwordError, setPasswordError] = useState("");
  const [isAiChatOpen, setIsAiChatOpen] = useState(false);
  const [isGenLogOpen, setIsGenLogOpen] = useState(false);
  const [isScriptViewerOpen, setIsScriptViewerOpen] = useState(false);
  const [creationGuide, setCreationGuide] = useState(null);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [currentPointSummary, setCurrentPointSummary] = useState(null);
  const [navDrawerOpen, setNavDrawerOpen] = useState({
    workspaceCurrent: true,
    workspaceMaking: true,
    workspaceGenerate: true,
    workspaceAssets: true,
    workspaceTools: true,
    projectPlan: true,
    projectGenerate: true,
    projectAssets: true,
    homeOverview: true,
  });
  const navScrollRef = useRef(null);
  const [navScrollState, setNavScrollState] = useState({
    canScroll: false,
    atTop: true,
    atBottom: true,
  });

  const updateNavScrollState = useCallback(() => {
    const node = navScrollRef.current;
    if (!node) return;
    const maxScrollTop = Math.max(0, node.scrollHeight - node.clientHeight);
    const nextState = {
      canScroll: maxScrollTop > 6,
      atTop: node.scrollTop <= 4,
      atBottom: node.scrollTop >= maxScrollTop - 4,
    };
    setNavScrollState((prev) => (
      prev.canScroll === nextState.canScroll
      && prev.atTop === nextState.atTop
      && prev.atBottom === nextState.atBottom
        ? prev
        : nextState
    ));
  }, []);

  const refreshCurrentPoints = useCallback(async () => {
    if (!userId) {
      setCurrentPointSummary(null);
      return;
    }
    try {
      const data = await authenticatedFetch(`${API_HOST}/api/points/me`);
      setCurrentPointSummary(data || null);
    } catch (error) {
      console.warn("[UserPoints] 当前积分加载失败", error);
    }
  }, [userId]);

  useEffect(() => {
    refreshCurrentPoints();
    const handleRefresh = () => refreshCurrentPoints();
    window.addEventListener("user-points-refresh", handleRefresh);
    return () => {
      window.removeEventListener("user-points-refresh", handleRefresh);
    };
  }, [refreshCurrentPoints]);

  // 窄屏时确保抽屉默认关闭（初始状态已关闭，仅订阅后续断点变化）
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => {
      if (e.matches) setIsSidebarOpen(false);
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // 抽屉导航：路由变化后在渲染期同步关闭，避免页面被导航层持续覆盖（render-adjust 模式）
  const [isStyleMenuOpen, setIsStyleMenuOpen] = useState(false);
  const themeMenuVisibility = useDelayedUnmount(isThemeMenuOpen, 110);
  const sidebarModelMenuVisibility = useDelayedUnmount(isModelMenuOpen, 110);
  const sidebarStyleMenuVisibility = useDelayedUnmount(isStyleMenuOpen, 110);
  const moreMenuVisibility = useDelayedUnmount(isMoreMenuOpen, 110);
  const [prevPathname, setPrevPathname] = useState(location.pathname);
  if (prevPathname !== location.pathname) {
    setPrevPathname(location.pathname);
    setIsSidebarOpen(false);
    setIsModelMenuOpen(false);
    setIsStyleMenuOpen(false);
    setIsThemeMenuOpen(false);
  }

  useEffect(() => {
    const closeSidebar = () => setIsSidebarOpen(false);
    window.addEventListener("app-sidebar-close", closeSidebar);
    return () => window.removeEventListener("app-sidebar-close", closeSidebar);
  }, []);
  
  // 全局画风状态 - 使用 Context
  const { currentStyle, setCurrentStyle } = useStyle();

  // 判断当前是否在工作台模式 (有 episodeId)
  const workspaceMatch = useMatch("/project/:projectId/episode/:episodeId/:tab?");
  const roughCutMatch = useMatch("/project/:projectId/episode/:episodeId/rough-cut");
  const isWorkspaceMode = !!workspaceMatch || !!roughCutMatch;
  const currentTab = roughCutMatch ? "rough-cut" : (workspaceMatch?.params?.tab || "mini-storyboard");

  // 判断当前是否在项目级别（项目详情页或资产管理页）
  const projectMatch = useMatch("/project/:projectId");
  const projectAssetsMatch = useMatch("/project/:projectId/assets/:assetType");
  const projectOutlineMatch = useMatch("/project/:projectId/outline");
  const projectCostMatch = useMatch("/project/:projectId/cost");
  const scriptAnalysisMatch = useMatch("/project/:projectId/script-analysis");
  const storyStructureMatch = useMatch("/project/:projectId/story-structure");
  const actingGuideMatch = useMatch("/project/:projectId/acting-guide");
  const referenceVideoReverseMatch = useMatch("/project/:projectId/reference-video-reverse");
  const adaptationAgentEpisodesMatch = useMatch("/project/:projectId/adaptation-agent/episodes");
  const adaptationAgentMatch = useMatch("/project/:projectId/adaptation-agent");
  const novelAgentMatch = useMatch("/project/:projectId/novel-agent");
  const oneClickExplainerMatch = useMatch("/project/:projectId/one-click-explainer");
  const dialogueAutoMatch = useMatch("/project/:projectId/dialogue-auto");
  const projectMusicMatch = useMatch("/project/:projectId/music");
  const narrationProductionMatch = useMatch("/project/:projectId/narration-production");
  const videoRepaintMatch = useMatch("/project/:projectId/video-repaint");
  const interactiveStoryMatch = useMatch("/project/:projectId/interactive-story");
  const interactiveStoryRouteActive = Boolean(interactiveStoryMatch);
  const isProjectMode = !!(projectMatch || projectAssetsMatch || projectOutlineMatch || projectCostMatch || scriptAnalysisMatch || storyStructureMatch || actingGuideMatch || referenceVideoReverseMatch || adaptationAgentEpisodesMatch || adaptationAgentMatch || novelAgentMatch || oneClickExplainerMatch || dialogueAutoMatch || projectMusicMatch || narrationProductionMatch || videoRepaintMatch || interactiveStoryMatch) && !isWorkspaceMode;
  const currentProjectId = projectMatch?.params?.projectId || projectAssetsMatch?.params?.projectId || projectOutlineMatch?.params?.projectId || projectCostMatch?.params?.projectId || scriptAnalysisMatch?.params?.projectId || storyStructureMatch?.params?.projectId || actingGuideMatch?.params?.projectId || referenceVideoReverseMatch?.params?.projectId || adaptationAgentEpisodesMatch?.params?.projectId || adaptationAgentMatch?.params?.projectId || novelAgentMatch?.params?.projectId || oneClickExplainerMatch?.params?.projectId || dialogueAutoMatch?.params?.projectId || projectMusicMatch?.params?.projectId || narrationProductionMatch?.params?.projectId || videoRepaintMatch?.params?.projectId || interactiveStoryMatch?.params?.projectId;
  const currentAssetType = projectAssetsMatch?.params?.assetType;

  // 获取当前项目的权限（工作台模式或项目模式）
  const activeProjectId = workspaceMatch?.params?.projectId || roughCutMatch?.params?.projectId || currentProjectId;
  const floatingPointBalanceRef = useRef(null);
  useEffect(() => {
    const badge = floatingPointBalanceRef.current;
    const content = badge?.parentElement;
    if (!badge || !content) return undefined;
    // Standalone town headers share this row with fixed controls. Measure the real
    // badge width, including longer balances and the responsive "积分" label.
    const updateWidth = () => content.style.setProperty(
      '--floating-point-balance-width', `${badge.getBoundingClientRect().width}px`,
    );
    updateWidth();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateWidth);
    observer?.observe(badge);
    return () => {
      observer?.disconnect();
      content.style.removeProperty('--floating-point-balance-width');
    };
  }, [activeProjectId]);
  const creationGuideScope = `${userId}:${activeProjectId || ''}`;
  const [previousCreationGuideScope, setPreviousCreationGuideScope] = useState(creationGuideScope);
  if (previousCreationGuideScope !== creationGuideScope) {
    setPreviousCreationGuideScope(creationGuideScope);
    setCreationGuide(null);
  }
  const activeEpisodeId = workspaceMatch?.params?.episodeId || roughCutMatch?.params?.episodeId;
  const workspaceRouteMatch = workspaceMatch || roughCutMatch;
  const workspaceProjectId = workspaceRouteMatch?.params?.projectId;
  const workspaceEpisodeId = workspaceRouteMatch?.params?.episodeId;
  const { can, role: activeProjectRole } = useProjectPermissions(activeProjectId);
  const [adaptationProductionReady, setAdaptationProductionReady] = useState(false);
  const [activeProject, setActiveProject] = useState(null);
  const [projectEpisodes, setProjectEpisodes] = useState([]);
  const [visualStyleReferences, setVisualStyleReferences] = useState([]);

  const activeModel = MODEL_MENU_PROVIDERS.find((p) => p.id === currentProvider) || MODEL_MENU_PROVIDERS[0];
  const activeModelColor = activeModel.color || theme.colors.accentPrimary;
  const activeDreamflyLlmOption = currentProvider === DREAMFLY_LLM_PROVIDER_ID
    ? dreamflyLlmCatalog.models.find((option) => option.id === dreamflyLlmModel)
    : null;
  const activeModelDisplayName = currentProvider === DREAMFLY_LLM_PROVIDER_ID
    ? formatDreamflyLlmDisplayName(dreamflyLlmModel, activeDreamflyLlmOption)
    : activeModel.name;
  const currentThemeData = getTheme();
  const activeVisualReferenceMarket = isOverseasProject(activeProject) ? "overseas" : "domestic";
  const visualStyleOptions = useMemo(
    () => attachVisualStyleReferences(STYLE_OPTIONS, visualStyleReferences),
    [visualStyleReferences]
  );

  const currentEpisode = useMemo(
    () => projectEpisodes.find((episode) => String(episode.id) === String(activeEpisodeId)) || null,
    [projectEpisodes, activeEpisodeId]
  );

  const activeProjectName = activeProject?.name || activeProject?.title || (activeProjectId ? `项目 ${activeProjectId}` : "");
  const activeProjectVisualStyleMode = getProjectVisualStyleMode(activeProject);
  const activeProjectVisualStyle = normalizeVisualStyleKey(getProjectVisualStyleValue(activeProject, localStorage.getItem("gen_style") || currentStyle || "modern_cdrama"));
  const activeProjectStyleLabel = activeProjectVisualStyleMode === "none"
    ? "无固定风格"
    : (visualStyleOptions.find(s => s.value === activeProjectVisualStyle)?.label || activeProjectVisualStyle);
  const isProjectStyleLocked = Boolean(activeProjectId && activeProjectVisualStyleMode !== "legacy");
  const activeProjectVideoRatio = normalizeProjectVideoRatio(activeProject?.defaultVideoRatio || activeProject?.videoRatio || activeProject?.defaultAspectRatio);
  const activeEpisodeLabel = useMemo(
    () => normalizeEpisodeLabel(currentEpisode, activeEpisodeId),
    [currentEpisode, activeEpisodeId]
  );
  const activeProjectIsNarration = isNarrationProject(activeProject);
  const narrationProjectActive = activeProjectIsNarration || Boolean(narrationProductionMatch);
  const activeProjectIsRepaint = isRepaintProject(activeProject);
  const repaintProjectActive = activeProjectIsRepaint || Boolean(videoRepaintMatch);
  const activeProjectIsInteractiveGame = isInteractiveGameProject(activeProject);
  const interactiveGameProjectActive = activeProjectIsInteractiveGame || interactiveStoryRouteActive;
  const canEditProjectType = String(activeProjectRole || activeProject?.currentUserRole || "").toLowerCase() === "owner";
  const activeProjectEntryPath = getProjectEntryPath({
    ...activeProject,
    id: activeProjectId,
    productionMode: interactiveGameProjectActive
      ? PROJECT_PRODUCTION_MODE_INTERACTIVE_GAME
      : repaintProjectActive
        ? PROJECT_PRODUCTION_MODE_REPAINT
        : narrationProjectActive
          ? PROJECT_PRODUCTION_MODE_NARRATION
          : activeProject?.productionMode,
  });

  useEffect(() => {
    if (!activeProjectId || interactiveGameProjectActive) return;
    if (activeProjectVisualStyleMode === "fixed" || activeProjectVisualStyleMode === "none") {
      setCurrentStyle(activeProjectVisualStyle, { persist: false });
      return;
    }
    setCurrentStyle(normalizeVisualStyleKey(localStorage.getItem("gen_style"), "modern_cdrama"), { persist: false });
  }, [activeProjectId, activeProjectVisualStyle, activeProjectVisualStyleMode, interactiveGameProjectActive, setCurrentStyle]);

  useEffect(() => {
    if (interactiveGameProjectActive) {
      setVisualStyleReferences([]);
      return undefined;
    }
    let cancelled = false;
    fetchPublicVisualReferences({ category: "visual_style", mediaType: "image", market: activeVisualReferenceMarket })
      .then((data) => {
        if (!cancelled) setVisualStyleReferences(data?.items || []);
      })
      .catch((error) => {
        console.warn("[App] visual style references load failed", error);
        if (!cancelled) setVisualStyleReferences([]);
      });
    return () => {
      cancelled = true;
    };
  }, [activeVisualReferenceMarket, interactiveGameProjectActive]);

  const currentViewLabel = useMemo(() => {
    if (isWorkspaceMode) return WORKSPACE_TAB_LABELS[currentTab] || "单集工作区";
    if (projectAssetsMatch) return PROJECT_ASSET_LABELS[currentAssetType] || "资产中心";
    if (projectOutlineMatch) return PROJECT_VIEW_LABELS.outline;
    if (projectCostMatch) return PROJECT_VIEW_LABELS.cost;
    if (scriptAnalysisMatch) return PROJECT_VIEW_LABELS.script;
    if (storyStructureMatch) return PROJECT_VIEW_LABELS.structure;
    if (actingGuideMatch) return "AI表演导演表";
    if (referenceVideoReverseMatch) return "参考视频反拆";
    if (adaptationAgentEpisodesMatch) return PROJECT_VIEW_LABELS.production;
    if (adaptationAgentMatch) return PROJECT_VIEW_LABELS.adaptation;
    if (oneClickExplainerMatch) return "一键解说剧";
    if (dialogueAutoMatch) return "对话短剧自动流";
    if (novelAgentMatch) return PROJECT_VIEW_LABELS.novel;
    if (projectMusicMatch) return "音乐音频";
    if (narrationProductionMatch) return PROJECT_VIEW_LABELS.narration;
    if (videoRepaintMatch) return PROJECT_VIEW_LABELS.repaint;
    if (interactiveStoryMatch) return "互动游戏";
    if (projectMatch) return PROJECT_VIEW_LABELS.overview;
    if (location.pathname === "/gallery") return "作品展廊";
    if (location.pathname === "/hongguo-rankings") return "红果榜单";
    if (location.pathname === "/help") return "使用指南";
    return "项目列表";
  }, [
    adaptationAgentEpisodesMatch,
    adaptationAgentMatch,
    actingGuideMatch,
    currentAssetType,
    currentTab,
    dialogueAutoMatch,
    interactiveStoryMatch,
    isWorkspaceMode,
    location.pathname,
    novelAgentMatch,
    narrationProductionMatch,
    oneClickExplainerMatch,
    projectAssetsMatch,
    projectCostMatch,
    projectMatch,
    projectMusicMatch,
    projectOutlineMatch,
    referenceVideoReverseMatch,
    scriptAnalysisMatch,
    storyStructureMatch,
    videoRepaintMatch,
  ]);

  const buildWorkspacePath = useCallback((tabName, episodeId = workspaceEpisodeId) => {
    if (!workspaceProjectId || !episodeId) return "/";
    if (tabName === "rough-cut") {
      return `/project/${workspaceProjectId}/episode/${episodeId}/rough-cut`;
    }
    return `/project/${workspaceProjectId}/episode/${episodeId}/${tabName}`;
  }, [workspaceEpisodeId, workspaceProjectId]);

  const handleEpisodeSwitch = useCallback((nextEpisodeId) => {
    if (!nextEpisodeId || !workspaceProjectId) return;
    navigate(buildWorkspacePath(currentTab || "storyboard", nextEpisodeId));
  }, [buildWorkspacePath, currentTab, navigate, workspaceProjectId]);

  const contextFeatureGroups = useMemo(() => {
    if (isWorkspaceMode && workspaceProjectId && workspaceEpisodeId) {
      const projectToolItem = (key, label, icon, path) => ({
        key,
        label,
        icon,
        path,
        active: false,
      });
      const workspaceItem = (tabName, label, icon) => ({
        key: tabName,
        label,
        icon,
        path: buildWorkspacePath(tabName),
        active: currentTab === tabName,
      });
      if (narrationProjectActive) {
        return [
          {
            title: "快捷入口",
            items: [
              { key: "script-viewer", label: "剧本快览", icon: BookOpen, action: "script-viewer", selected: isScriptViewerOpen },
              workspaceItem("resources", "资源中心", History),
              { key: "text-assistant", label: "文字助手", icon: MessageSquare, action: "text-assistant", selected: isAiChatOpen },
            ],
          },
          {
            title: "解说剧流程",
            items: [
              projectToolItem("narration-production", "解说剧生产", ScrollText, `/project/${workspaceProjectId}/narration-production`),
              projectToolItem("script", "剧本中心", BookOpen, `/project/${workspaceProjectId}/script-analysis`),
            ],
          },
          {
            title: "本集生产",
            items: [
              workspaceItem("upload", "本集剧本", BookOpen),
              workspaceItem("mini-storyboard", "小故事板", ScrollText),
              workspaceItem("rough-cut", "粗剪台", Scissors),
            ],
          },
          {
            title: "资产中心",
            items: [
              workspaceItem("character", "角色工坊", Users),
              workspaceItem("scene", "场景工坊", ImageIcon),
              workspaceItem("prop", "道具工坊", Box),
            ],
          },
        ];
      }
      return [
        {
          title: "快捷入口",
          items: [
            { key: "script-viewer", label: "剧本快览", icon: BookOpen, action: "script-viewer", selected: isScriptViewerOpen },
            workspaceItem("resources", "资源中心", History),
            { key: "text-assistant", label: "文字助手", icon: MessageSquare, action: "text-assistant", selected: isAiChatOpen },
          ],
        },
        {
          title: "剧本/策划",
          items: [
            projectToolItem("outline", "剧情大纲", FileText, `/project/${workspaceProjectId}/outline`),
            projectToolItem("script", "剧本中心", BookOpen, `/project/${workspaceProjectId}/script-analysis`),
            projectToolItem("structure", "故事骨架", Workflow, `/project/${workspaceProjectId}/story-structure`),
            projectToolItem("adaptation", "智能改编", Bot, `/project/${workspaceProjectId}/adaptation-agent`),
            projectToolItem("novel", "小说转剧本", MessageSquare, `/project/${workspaceProjectId}/novel-agent`),
            projectToolItem("explainer", "一键解说剧", ScrollText, `/project/${workspaceProjectId}/one-click-explainer`),
            projectToolItem("dialogue", "对话短剧自动流", Clapperboard, `/project/${workspaceProjectId}/dialogue-auto`),
            projectToolItem("acting-guide", "AI表演导演表", Clapperboard, `/project/${workspaceProjectId}/acting-guide`),
          ],
        },
        {
          title: "本集",
          items: [
            workspaceItem("upload", "本集剧本", BookOpen),
          ],
        },
        {
          title: "故事制作",
          items: [
            workspaceItem("mini-storyboard", "小故事板", ScrollText),
            workspaceItem("storyboard-board", "故事板导演台", Film),
            workspaceItem("storyboard", "分镜编辑器", Clapperboard),
            workspaceItem("canvas", "画布模式", Workflow),
            workspaceItem("rough-cut", "粗剪台", Scissors),
          ],
        },
        {
          title: "生成工具",
          items: [
            workspaceItem("imageagent", "图片生成", Palette),
            workspaceItem("videoagent", "视频生成", Film),
            workspaceItem("musicagent", "音乐音频", Music),
          ],
        },
        {
          title: "资产中心",
          items: [
            workspaceItem("character", "角色工坊", Users),
            workspaceItem("scene", "场景工坊", ImageIcon),
            workspaceItem("prop", "道具工坊", Box),
          ],
        },
        {
          title: "项目工具",
          items: [
            workspaceItem("education", "教学中心", BookOpen),
            workspaceItem("motion-storyboard", "动态分镜", Swords),
            workspaceItem("orbit", "3D 视角", Rotate3D),
          ],
        },
      ];
    }

    if (!activeProjectId) return [];
    const projectItem = (key, label, icon, path, active) => ({
      key,
      label,
      icon,
      path,
      active: Boolean(active),
    });
    if (interactiveGameProjectActive) {
      return [
        {
          title: "互动游戏",
          items: [
            projectItem("interactive-story", "剧情画布", Gamepad2, `/project/${activeProjectId}/interactive-story`, interactiveStoryMatch),
          ],
        },
      ];
    }
    if (repaintProjectActive) {
      return [
        {
          title: "影视转绘",
          items: [
            projectItem("video-repaint", "影视转绘", Video, `/project/${activeProjectId}/video-repaint`, videoRepaintMatch),
          ],
        },
        {
          title: "目标资产",
          items: [
            projectItem("character", "角色工坊", Users, `/project/${activeProjectId}/assets/character`, projectAssetsMatch && currentAssetType === "character"),
            projectItem("scene", "场景工坊", ImageIcon, `/project/${activeProjectId}/assets/scene`, projectAssetsMatch && currentAssetType === "scene"),
            projectItem("resources", "资源中心", History, `/project/${activeProjectId}/assets/resources`, projectAssetsMatch && currentAssetType === "resources"),
          ],
        },
        {
          title: "项目",
          items: [
            projectItem("cost", "费用统计", LayoutDashboard, `/project/${activeProjectId}/cost`, projectCostMatch),
          ],
        },
      ];
    }
    if (narrationProjectActive) {
      return [
        {
          title: "快捷入口",
          items: [
            { key: "script-viewer", label: "剧本快览", icon: BookOpen, action: "script-viewer", selected: isScriptViewerOpen },
            projectItem("resources", "资源中心", History, `/project/${activeProjectId}/assets/resources`, projectAssetsMatch && currentAssetType === "resources"),
            { key: "text-assistant", label: "文字助手", icon: MessageSquare, action: "text-assistant", selected: isAiChatOpen },
          ],
        },
        {
          title: "解说剧流程",
          items: [
            projectItem("narration-production", "解说剧生产", ScrollText, `/project/${activeProjectId}/narration-production`, narrationProductionMatch),
            projectItem("script", "剧本中心", BookOpen, `/project/${activeProjectId}/script-analysis`, scriptAnalysisMatch),
          ],
        },
        {
          title: "资产中心",
          items: [
            projectItem("character", "角色工坊", Users, `/project/${activeProjectId}/assets/character`, projectAssetsMatch && currentAssetType === "character"),
            projectItem("scene", "场景工坊", ImageIcon, `/project/${activeProjectId}/assets/scene`, projectAssetsMatch && currentAssetType === "scene"),
            projectItem("prop", "道具工坊", Box, `/project/${activeProjectId}/assets/prop`, projectAssetsMatch && currentAssetType === "prop"),
            projectItem("resources", "资源中心", History, `/project/${activeProjectId}/assets/resources`, projectAssetsMatch && currentAssetType === "resources"),
          ],
        },
        {
          title: "工具",
          items: [
            projectItem("music", "音乐音频", Music, `/project/${activeProjectId}/music`, projectMusicMatch),
            projectItem("cost", "费用统计", LayoutDashboard, `/project/${activeProjectId}/cost`, projectCostMatch),
          ],
        },
      ];
    }
    return [
      {
        title: "快捷入口",
        items: [
          { key: "script-viewer", label: "剧本快览", icon: BookOpen, action: "script-viewer", selected: isScriptViewerOpen },
          projectItem("resources", "资源中心", History, `/project/${activeProjectId}/assets/resources`, projectAssetsMatch && currentAssetType === "resources"),
          { key: "text-assistant", label: "文字助手", icon: MessageSquare, action: "text-assistant", selected: isAiChatOpen },
        ],
      },
      {
        title: "项目",
        items: [
          projectItem("overview", "项目总览", Layers, `/project/${activeProjectId}`, projectMatch),
          projectItem("outline", "剧情大纲", FileText, `/project/${activeProjectId}/outline`, projectOutlineMatch),
          projectItem("script", "剧本中心", BookOpen, `/project/${activeProjectId}/script-analysis`, scriptAnalysisMatch),
          projectItem("structure", "故事骨架", Workflow, `/project/${activeProjectId}/story-structure`, storyStructureMatch),
        ],
      },
      {
        title: "自动流",
        items: [
          projectItem("adaptation", "智能改编", Bot, `/project/${activeProjectId}/adaptation-agent`, adaptationAgentMatch),
          projectItem("novel", "小说转剧本", MessageSquare, `/project/${activeProjectId}/novel-agent`, novelAgentMatch),
          projectItem("explainer", "一键解说剧", ScrollText, `/project/${activeProjectId}/one-click-explainer`, oneClickExplainerMatch),
          projectItem("dialogue", "对话短剧自动流", Clapperboard, `/project/${activeProjectId}/dialogue-auto`, dialogueAutoMatch),
          projectItem("production", "分集生产台", Film, `/project/${activeProjectId}/adaptation-agent/episodes`, adaptationAgentEpisodesMatch),
        ],
      },
      {
        title: "资产",
        items: [
          projectItem("character", "角色工坊", Users, `/project/${activeProjectId}/assets/character`, projectAssetsMatch && currentAssetType === "character"),
          projectItem("scene", "场景工坊", ImageIcon, `/project/${activeProjectId}/assets/scene`, projectAssetsMatch && currentAssetType === "scene"),
          projectItem("prop", "道具工坊", Box, `/project/${activeProjectId}/assets/prop`, projectAssetsMatch && currentAssetType === "prop"),
          projectItem("fight", "招式工坊", Swords, `/project/${activeProjectId}/assets/fight`, projectAssetsMatch && currentAssetType === "fight"),
          projectItem("image", "图片资源", Palette, `/project/${activeProjectId}/assets/image`, projectAssetsMatch && currentAssetType === "image"),
        ],
      },
      {
        title: "工具",
        items: [
          projectItem("music", "音乐音频", Music, `/project/${activeProjectId}/music`, projectMusicMatch),
          projectItem("acting-guide", "AI表演导演表", Clapperboard, `/project/${activeProjectId}/acting-guide`, actingGuideMatch),
          projectItem("reference-video-reverse", "参考视频反拆", Video, `/project/${activeProjectId}/reference-video-reverse`, referenceVideoReverseMatch),
          projectItem("cost", "费用统计", LayoutDashboard, `/project/${activeProjectId}/cost`, projectCostMatch),
        ],
      },
    ];
  }, [
    activeProjectId,
    adaptationAgentEpisodesMatch,
    adaptationAgentMatch,
    actingGuideMatch,
    buildWorkspacePath,
    currentAssetType,
    currentTab,
    dialogueAutoMatch,
    isAiChatOpen,
    interactiveGameProjectActive,
    isScriptViewerOpen,
    isWorkspaceMode,
    interactiveStoryMatch,
    novelAgentMatch,
    narrationProductionMatch,
    narrationProjectActive,
    oneClickExplainerMatch,
    projectAssetsMatch,
    projectCostMatch,
    projectMatch,
    projectMusicMatch,
    projectOutlineMatch,
    referenceVideoReverseMatch,
    repaintProjectActive,
    scriptAnalysisMatch,
    storyStructureMatch,
    videoRepaintMatch,
    workspaceEpisodeId,
    workspaceProjectId,
  ]);

  const handleContextFeatureAction = useCallback((action) => {
    if (action === "creation-guide" || action === "prompt-optimizer") {
      setCreationGuide({ open: true, mode: action === "prompt-optimizer" ? "optimize" : "learn" });
      setIsScriptViewerOpen(false);
      setIsAiChatOpen(false);
      setIsGenLogOpen(false);
      return;
    }
    if (action === "script-viewer") {
      const nextOpen = !isScriptViewerOpen;
      setIsScriptViewerOpen(nextOpen);
      if (nextOpen) {
        setCreationGuide((current) => current ? { ...current, open: false } : null);
        setIsAiChatOpen(false);
        setIsGenLogOpen(false);
      }
      return;
    }
    if (action === "text-assistant") {
      const nextOpen = !isAiChatOpen;
      setIsAiChatOpen(nextOpen);
      if (nextOpen) {
        setCreationGuide((current) => current ? { ...current, open: false } : null);
        setIsScriptViewerOpen(false);
        setIsGenLogOpen(false);
      }
    }
  }, [isAiChatOpen, isScriptViewerOpen, setIsAiChatOpen, setIsGenLogOpen, setIsScriptViewerOpen]);

  const handleProviderChange = (providerId) => {
    providerId = normalizeModelMenuProvider(providerId);
    const switchingToDreamfly = providerId === DREAMFLY_LLM_PROVIDER_ID
      && currentProvider !== DREAMFLY_LLM_PROVIDER_ID;
    setCurrentProvider(providerId);
    localStorage.setItem("ai_provider", providerId); 
    if (MODEL_MENU_PROVIDERS.some(provider => provider.id === providerId
      && (!Array.isArray(provider.capabilities) || provider.capabilities.includes('image')))) {
      setStoredImageProvider(providerId);
    }
    window.dispatchEvent(new CustomEvent("ai-provider-change", { detail: { provider: providerId } }));
    setIsModelMenuOpen(false);
    if (providerId === DREAMFLY_LLM_PROVIDER_ID) {
      if (switchingToDreamfly) {
        setDreamflyLlmCatalog((current) => ({ ...current, loading: true, error: "" }));
      }
      setIsSidebarOpen(true);
    }
  };

  const handleDreamflyLlmModelChange = useCallback((modelId) => {
    const nextModel = String(modelId || "").trim();
    const safeModel = isDreamflyImageApiModel(nextModel) ? "" : nextModel;
    setDreamflyLlmModel(safeModel);
    if (safeModel) {
      localStorage.setItem(DREAMFLY_LLM_MODEL_STORAGE_KEY, safeModel);
    } else {
      localStorage.removeItem(DREAMFLY_LLM_MODEL_STORAGE_KEY);
    }
    window.dispatchEvent(new CustomEvent("dreamfly-llm-model-change", {
      detail: { model: safeModel },
    }));
  }, []);

  useEffect(() => {
    if (currentProvider !== DREAMFLY_LLM_PROVIDER_ID) return undefined;
    let cancelled = false;

    fetchDreamflyLlmModels()
      .then(({ models, defaultModel }) => {
        if (cancelled) return;
        setDreamflyLlmCatalog({ models, loading: false, error: "" });
        setDreamflyLlmModel((currentModel) => {
          const knownIds = new Set(models.map((option) => option.id));
          const nextModel = knownIds.has(currentModel)
            ? currentModel
            : (defaultModel || models[0]?.id || "");
          if (nextModel) {
            localStorage.setItem(DREAMFLY_LLM_MODEL_STORAGE_KEY, nextModel);
          } else {
            localStorage.removeItem(DREAMFLY_LLM_MODEL_STORAGE_KEY);
          }
          return nextModel;
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setDreamflyLlmCatalog({
          models: [],
          loading: false,
          error: `无法加载模型目录，保留已有选择；未选时不可调用：${error.message}`,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [currentProvider]);

  useEffect(() => {
    const syncProvider = () => {
      const storedProvider = localStorage.getItem("ai_provider");
      const provider = normalizeModelMenuProvider(storedProvider);
      setCurrentProvider(provider);
      localStorage.setItem("ai_provider", provider);
      // 旧 GPT-6 组合只迁移一次，之后保留用户单独选择的图片线路。
      if (isLegacyAstraProvider(storedProvider)) {
        setStoredImageProvider(provider);
      }
    };
    syncProvider();
    window.addEventListener("storage", syncProvider);
    window.addEventListener("ai-provider-change", syncProvider);
    return () => {
      window.removeEventListener("storage", syncProvider);
      window.removeEventListener("ai-provider-change", syncProvider);
    };
  }, []);

  useEffect(() => {
    const syncDreamflyLlmModel = () => {
      const storedModel = String(localStorage.getItem(DREAMFLY_LLM_MODEL_STORAGE_KEY) || "").trim();
      setDreamflyLlmModel(isDreamflyImageApiModel(storedModel) ? "" : storedModel);
    };
    window.addEventListener("storage", syncDreamflyLlmModel);
    window.addEventListener("dreamfly-llm-model-change", syncDreamflyLlmModel);
    return () => {
      window.removeEventListener("storage", syncDreamflyLlmModel);
      window.removeEventListener("dreamfly-llm-model-change", syncDreamflyLlmModel);
    };
  }, []);

  // activeProjectId 清空时在渲染期同步重置项目上下文（render-adjust 模式，替代 effect 内 setState）
  const [prevActiveProjectId, setPrevActiveProjectId] = useState(activeProjectId);
  if (prevActiveProjectId !== activeProjectId) {
    setPrevActiveProjectId(activeProjectId);
    setIsProjectTypeSettingsOpen(false);
    if (!activeProjectId) {
      setActiveProject(null);
      setProjectEpisodes([]);
    }
  }

  useEffect(() => {
    if (!activeProjectId) {
      return undefined;
    }

    let cancelled = false;
    const loadProjectContext = async () => {
      try {
        const [projectListResult, episodesResult] = await Promise.allSettled([
          authenticatedFetch(`${API_HOST}/api/project/list`),
          interactiveStoryRouteActive
            ? Promise.resolve([])
            : authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${activeProjectId}`),
        ]);
        if (cancelled) return;
        if (projectListResult.status === "fulfilled" && Array.isArray(projectListResult.value)) {
          const foundProject = projectListResult.value.find(
            (project) => String(project.id) === String(activeProjectId),
          ) || null;
          setActiveProject(foundProject);
          try {
            if (foundProject) {
              localStorage.setItem(`project_runtime_profile_${activeProjectId}`, JSON.stringify(foundProject));
              window.dispatchEvent(new CustomEvent("project-runtime-profile-change", { detail: { projectId: activeProjectId, project: foundProject } }));
            } else {
              // Only a successful authoritative project list may prove that the
              // project no longer exists. A network failure must retain the last
              // known provider so it cannot silently fall through to another one.
              localStorage.removeItem(`project_runtime_profile_${activeProjectId}`);
            }
          } catch {
            // localStorage 不可用时只影响前端默认选项，不影响后端按项目隔离。
          }
        }
        if (episodesResult.status === "fulfilled" && Array.isArray(episodesResult.value)) {
          setProjectEpisodes(episodesResult.value);
        }
      } catch {
        if (cancelled) return;
        setActiveProject(null);
        setProjectEpisodes([]);
      }
    };

    loadProjectContext();
    const onEpisodesChanged = () => loadProjectContext();
    window.addEventListener("episode-list-change", onEpisodesChanged);
    window.addEventListener("adaptation-production-state-change", onEpisodesChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("episode-list-change", onEpisodesChanged);
      window.removeEventListener("adaptation-production-state-change", onEpisodesChanged);
    };
  }, [activeProjectId, interactiveStoryRouteActive]);

  useEffect(() => {
    const handleProjectRuntimeProfileChange = (event) => {
      if (!event?.detail?.projectId || String(event.detail.projectId) !== String(activeProjectId)) return;
      setActiveProject((prev) => ({ ...(prev || {}), ...(event.detail.project || {}) }));
    };
    window.addEventListener("project-runtime-profile-change", handleProjectRuntimeProfileChange);
    return () => window.removeEventListener("project-runtime-profile-change", handleProjectRuntimeProfileChange);
  }, [activeProjectId]);

  useEffect(() => {
    if (!activeProjectId) {
      queueMicrotask(() => setAdaptationProductionReady(false));
      queueMicrotask(() => setIsScriptViewerOpen(false));
      return undefined;
    }
    let cancelled = false;
    const loadProductionState = () => {
      authenticatedFetch(`${API_HOST}/api/adaptation-agent/projects/${activeProjectId}/production-state`)
        .then((data) => {
          if (!cancelled) setAdaptationProductionReady(Boolean(data?.ready));
        })
        .catch(() => {
          if (!cancelled) setAdaptationProductionReady(false);
        });
    };
    loadProductionState();
    window.addEventListener("adaptation-production-state-change", loadProductionState);
    return () => {
      cancelled = true;
      window.removeEventListener("adaptation-production-state-change", loadProductionState);
    };
  }, [activeProjectId]);

  useEffect(() => {
    const node = navScrollRef.current;
    if (!node) return undefined;

    let frameId = window.requestAnimationFrame(updateNavScrollState);
    const scheduleUpdate = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateNavScrollState);
    };

    const resizeObserver = typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(scheduleUpdate)
      : null;
    resizeObserver?.observe(node);

    window.addEventListener("resize", scheduleUpdate);
    scheduleUpdate();

    return () => {
      window.cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleUpdate);
    };
  }, [
    activeProjectId,
    adaptationProductionReady,
    currentAssetType,
    currentTab,
    isProjectMode,
    isSidebarOpen,
    isWorkspaceMode,
    location.pathname,
    location.search,
    updateNavScrollState,
  ]);

  const handleSwitchUser = () => {
    logout();
  };

  const handleSwitchUserConfirm = async () => {
    const name = switchUserInput.trim();
    if (!name) return;
    if (!adminPasswordInput) {
      setPasswordError("请输入密码");
      return;
    }
    try {
      const data = await loginWithPassword({ username: name, password: adminPasswordInput });
      window.location.href = getLoginLandingPath(data.user);
    } catch (e) {
      if (e.code === 409 || e.data?.reason === 'PASSWORD_NOT_SET') {
        setPasswordError("该账号还未设置密码，请退出后在登录页设置密码");
      } else {
        setPasswordError(e.message || "登录失败");
      }
    }
  };

  const handleThemeChange = (themeId) => {
    switchTheme(themeId);
    setIsThemeMenuOpen(false);
    toast.success(`已切换到 ${themes[themeId].name} 主题`);
  };

  const handleStyleChange = async (styleValue) => {
    const nextMode = isNoFixedVisualStyle(styleValue) ? "none" : "fixed";
    const styleLabel = nextMode === "none"
      ? "无固定风格"
      : (visualStyleOptions.find(s => s.value === styleValue)?.label || styleValue);
    setIsStyleMenuOpen(false);

    if (!activeProjectId || activeProjectVisualStyleMode === "legacy") {
      setCurrentStyle(styleValue);
      toast.success(`已切换到 ${styleLabel} 画风`);
      return;
    }

    try {
      const updated = await authenticatedFetch(`${API_HOST}/api/project/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId: Number(activeProjectId),
          visualStyleMode: nextMode,
          visualStyle: nextMode === "fixed" ? styleValue : null,
        }),
      });
      const project = updated?.data || updated;
      setActiveProject((prev) => ({ ...(prev || {}), ...(project || {}), visualStyleMode: nextMode, visualStyle: nextMode === "fixed" ? styleValue : null }));
      setCurrentStyle(nextMode === "fixed" ? styleValue : "custom", { persist: false });
      if (project) {
        localStorage.setItem(`project_runtime_profile_${activeProjectId}`, JSON.stringify(project));
        window.dispatchEvent(new CustomEvent("project-runtime-profile-change", { detail: { projectId: activeProjectId, project } }));
      }
      toast.success(`项目画风已保存为 ${styleLabel}`);
    } catch (error) {
      toast.error(`项目画风保存失败: ${error.message || error}`, 0);
    }
  };

  const handleStylePanelClick = () => {
    if (isProjectStyleLocked) {
      setIsStyleMenuOpen(false);
      if (activeProjectId) navigate(`/project/${activeProjectId}`);
      return;
    }
    setIsStyleMenuOpen(!isStyleMenuOpen);
  };

  const handleProjectTypeChange = async (selection) => {
    if (!activeProjectId || !activeProject) {
      throw new Error("当前项目信息尚未加载完成，请稍后重试");
    }
    const previousProject = activeProject;
    const payload = buildProjectTypeUpdatePayload({
      projectId: activeProjectId,
      selection,
      currentProject: previousProject,
    });
    const updated = await authenticatedFetch(`${API_HOST}/api/project/update`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const mergedProject = {
      ...previousProject,
      ...payload,
      ...(updated || {}),
    };

    setActiveProject(mergedProject);
    rememberProjectProductionMode(activeProjectId, mergedProject.productionMode);
    try {
      localStorage.setItem(`project_runtime_profile_${activeProjectId}`, JSON.stringify(mergedProject));
      window.dispatchEvent(new CustomEvent("project-runtime-profile-change", {
        detail: { projectId: activeProjectId, project: mergedProject },
      }));
    } catch (cacheError) {
      console.warn("[ProjectTypeSettings] runtime cache sync failed", cacheError);
    }

    setIsProjectTypeSettingsOpen(false);
    const selectedOption = PROJECT_TYPE_OPTIONS.find((option) => option.key === selection);
    toast.success(`项目类型已切换为 ${selectedOption?.displayLabel || selectedOption?.title || "新类型"}`);

    const previousEntryPath = getProjectEntryPath(previousProject);
    const nextEntryPath = getProjectEntryPath(mergedProject);
    if (previousEntryPath !== nextEntryPath) {
      navigate(nextEntryPath, { replace: true });
    }
  };

  const handleProjectVideoRatioChange = async (ratio) => {
    const nextRatio = normalizeProjectVideoRatio(ratio);
    if (!activeProjectId || nextRatio === activeProjectVideoRatio) return;
    const previousProject = activeProject;
    const optimisticProject = { ...(activeProject || {}), defaultVideoRatio: nextRatio };
    setActiveProject(optimisticProject);
    try {
      const updated = await authenticatedFetch(`${API_HOST}/api/project/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId: Number(activeProjectId),
          defaultVideoRatio: nextRatio,
        }),
      });
      const project = updated?.data || updated || optimisticProject;
      const mergedProject = { ...optimisticProject, ...(project || {}), defaultVideoRatio: nextRatio };
      setActiveProject(mergedProject);
      localStorage.setItem(`project_runtime_profile_${activeProjectId}`, JSON.stringify(mergedProject));
      window.dispatchEvent(new CustomEvent("project-runtime-profile-change", {
        detail: { projectId: activeProjectId, project: mergedProject },
      }));
      toast.success(`视频画幅已设为 ${nextRatio}`);
    } catch (error) {
      setActiveProject(previousProject);
      toast.error(`视频画幅保存失败: ${error.message || error}`, 0);
    }
  };

  const toggleNavDrawer = useCallback((id) => {
    setNavDrawerOpen(prev => ({ ...prev, [id]: !(prev[id] ?? true) }));
  }, []);

  return (
    <div className="flex w-full h-screen overflow-hidden relative" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
      <div className="absolute inset-0 pointer-events-none z-[100]">
        <SnowOverlay isActive={isSnowing} />
      </div>

      {/* 抽屉导航 backdrop */}
      {sidebarBackdropVisibility.mounted && (
        <div
          className={`fixed inset-0 z-40 bg-[var(--overlay-light)] backdrop-blur-[2px] ${sidebarBackdropVisibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* === 左侧抽屉 Sidebar === */}
      <div
        className={`fixed inset-y-0 left-0 z-50 flex w-[324px] max-w-[88vw] flex-col transform transition-transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          backdropFilter: 'blur(12px)',
          borderRight: '1px solid var(--color-border-primary)',
          boxShadow: isSidebarOpen ? 'var(--shadow-modal)' : 'none',
          transitionDuration: 'var(--motion-duration-slow)',
          transitionTimingFunction: isSidebarOpen ? 'var(--motion-ease-enter)' : 'var(--motion-ease-exit)',
          willChange: 'transform',
        }}
      >
        {/* Header - 展开/收起按钮 + 主题选择器 */}
        <div className={`h-14 flex items-center shrink-0 px-3 gap-2.5 ${isSidebarOpen ? "justify-between" : "justify-center"}`}>
           {/* 主题选择器 (收起状态下显示图标) */}
           <div className={`relative ${isSidebarOpen ? "flex-1" : ""}`}>
             <button
               onClick={() => setIsThemeMenuOpen(!isThemeMenuOpen)}
               className={`w-full flex items-center transition-all duration-200 cursor-pointer group rounded-xl ${isSidebarOpen ? "px-3 py-2 justify-between h-10" : "p-2 justify-center aspect-square h-10"}`}
               style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}
               aria-expanded={isThemeMenuOpen}
               aria-haspopup="true"
               aria-label="切换主题"
             >
               <div className="flex items-center gap-2">
                 <div
                   className="w-4 h-4 rounded-full border flex-shrink-0"
                   style={{ backgroundColor: currentThemeData.colors.accentPrimary, borderColor: 'var(--color-border-primary)' }}
                 />
                 {isSidebarOpen && (
                   <span className="text-sm font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>{currentThemeData.name}</span>
                 )}
               </div>
               {isSidebarOpen && <ChevronDown size={14} style={{ color: 'var(--color-text-muted)' }} />}
             </button>
             {themeMenuVisibility.mounted && (
               <>
                 <div className="fixed inset-0 z-10" onClick={() => setIsThemeMenuOpen(false)} />
                 <div className={`absolute top-full mt-2 rounded-xl shadow-2xl z-20 overflow-hidden ${isSidebarOpen ? "left-0 w-full" : "left-full ml-2 w-56 top-0"} ${themeMenuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
                   style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                   {Object.entries(themes).map(([key, theme]) => (
                     <button
                       key={key}
                       onClick={() => handleThemeChange(key)}
                       className="w-full flex items-center gap-3 p-3 text-left transition-all duration-200 border-b last:border-0 cursor-pointer"
                       style={{ backgroundColor: currentTheme === key ? 'var(--color-accent-light)' : 'transparent', borderColor: 'var(--color-border-primary)' }}
                       onMouseEnter={(e) => { if (currentTheme !== key) e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; }}
                       onMouseLeave={(e) => { if (currentTheme !== key) e.currentTarget.style.backgroundColor = 'transparent'; }}
                     >
                       <div
                         className="w-5 h-5 rounded-full border flex-shrink-0"
                         style={{ background: `linear-gradient(135deg, ${theme.colors.accentPrimary} 50%, ${theme.colors.accentSecondary || theme.colors.accentPrimary} 50%)`, borderColor: 'var(--color-border-primary)' }}
                       />
                       <div className="flex-1 min-w-0">
                         <div className="text-xs" style={{ color: currentTheme === key ? theme.colors.accentSecondary : 'var(--color-text-secondary)' }}>{theme.name}</div>
                         <div className="text-2xs truncate" style={{ color: 'var(--color-text-muted)' }}>{theme.description}</div>
                       </div>
                       {currentTheme === key && <Check size={14} style={{ color: theme.colors.greenPrimary }} className="flex-shrink-0" />}
                     </button>
                   ))}
                 </div>
               </>
             )}
           </div>
           
           {/* 展开/收起按钮 */}
           <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} className="w-11 h-10 flex items-center justify-center rounded-xl transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]" style={{ color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }} aria-label={isSidebarOpen ? "收起侧边栏" : "展开侧边栏"} title={isSidebarOpen ? "收起侧边栏" : "展开侧边栏"}>
             {isSidebarOpen ? <PanelLeftClose size={19}/> : <PanelLeftOpen size={19}/>}
           </button>
        </div>

        <div className={`px-3 pt-1.5 pb-1.5 transition-all duration-300 shrink-0 ${isSidebarOpen ? "" : "flex justify-center"}`}>
          <button
            type="button"
            onClick={toggleLanguage}
            className={`w-full flex items-center transition-all duration-200 cursor-pointer rounded-xl ${isSidebarOpen ? "px-3.5 py-2.5 justify-between" : "p-2.5 justify-center aspect-square"}`}
            style={{
              backgroundColor: 'var(--color-bg-tertiary)',
              border: '1px solid var(--color-border-primary)',
              color: 'var(--color-text-secondary)'
            }}
            title={t("切换语言")}
            aria-label={t("切换语言")}
          >
            <span className="flex items-center gap-2 min-w-0">
              <Languages size={18} style={{ color: theme.colors.accentPrimary }} />
              {isSidebarOpen && <span className="text-sm font-bold truncate">{t("中英切换")}</span>}
            </span>
            {isSidebarOpen && (
              <span
                className="rounded-md px-2.5 py-1 text-xs font-bold"
                style={{ backgroundColor: 'var(--color-bg-primary)', color: theme.colors.accentPrimary }}
              >
                {languageLabel}
              </span>
            )}
          </button>
        </div>

        {/* AI 模型选择器 */}
        <div className={`px-3 pt-1 pb-1.5 transition-all duration-300 shrink-0 ${isSidebarOpen ? "" : "flex justify-center"}`}>
           <div className="relative w-full">
            <button onClick={() => setIsModelMenuOpen(!isModelMenuOpen)}
              className={`w-full flex items-center transition-all duration-200 cursor-pointer rounded-2xl ${isSidebarOpen ? "px-3 py-3 justify-between" : "p-2.5 justify-center aspect-square"}`}
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-primary)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; }}
              aria-expanded={isModelMenuOpen}
              aria-haspopup="true"
              aria-label="切换AI模型"
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                  <activeModel.icon size={20} style={{ color: activeModelColor }} />
                </div>
                {isSidebarOpen && (
                  <div className="flex flex-col items-start min-w-0 leading-tight">
                    <span className="text-xs font-bold truncate" style={{ color: 'var(--color-text-muted)' }}>AI 模型</span>
                    <span className="text-base font-bold truncate" style={{ color: activeModelColor }}>{activeModelDisplayName}</span>
                  </div>
                )}
              </div>
              {isSidebarOpen && <ChevronDown size={14} className={`transition-transform duration-200 ${isModelMenuOpen ? "rotate-180" : ""}`} style={{ color: 'var(--color-text-muted)' }} />}
            </button>
            {sidebarModelMenuVisibility.mounted && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsModelMenuOpen(false)}/>
                <div className={`absolute top-full mt-2 rounded-xl z-20 overflow-hidden py-1 ${isSidebarOpen ? "left-0 w-full" : "left-full ml-2 w-52 top-0"} ${sidebarModelMenuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
                  style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                  {MODEL_MENU_PROVIDERS.map((provider) => {
                    const providerColor = provider.color || theme.colors.accentPrimary;
                    const isSelected = currentProvider === provider.id;
                    return (
                      <button key={provider.id} onClick={() => handleProviderChange(provider.id)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer"
                        style={{ backgroundColor: isSelected ? `${providerColor}12` : 'transparent' }}
                        onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; }}
                        onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                        <div className="p-1.5 rounded-md flex-shrink-0" style={{ backgroundColor: isSelected ? `${providerColor}20` : `${providerColor}12` }}>
                          <provider.icon size={16} style={{ color: providerColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm" style={{ color: isSelected ? providerColor : 'var(--color-text-secondary)' }}>{provider.name}</div>
                          {provider.recommendation && <div className="text-2xs truncate" style={{ color: 'var(--color-text-muted)' }}>{provider.recommendation}</div>}
                        </div>
                        {isSelected && <Check size={14} style={{ color: providerColor }} className="flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
          {isSidebarOpen && currentProvider === DREAMFLY_LLM_PROVIDER_ID && (
            <DreamflyLlmModelSelect
              value={dreamflyLlmModel}
              options={dreamflyLlmCatalog.models}
              loading={dreamflyLlmCatalog.loading}
              error={dreamflyLlmCatalog.error}
              onChange={handleDreamflyLlmModelChange}
            />
          )}
        </div>

        {/* 画风选择器 */}
        {!interactiveGameProjectActive && <div className={`px-3 pt-1 pb-2 transition-all duration-300 shrink-0 ${isSidebarOpen ? "" : "flex justify-center"}`}>
          <div className="relative w-full">
            <button
              onClick={handleStylePanelClick}
              className={`w-full flex items-center transition-all duration-200 cursor-pointer rounded-2xl ${isSidebarOpen ? "px-3 py-3 justify-between" : "p-2.5 justify-center aspect-square"}`}
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                border: '1px solid var(--color-border-primary)',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; }}
              aria-expanded={!isProjectStyleLocked && isStyleMenuOpen}
              aria-haspopup={!isProjectStyleLocked ? "true" : undefined}
              aria-label={isProjectStyleLocked ? "查看项目画风设置" : "切换临时画风"}
              title={isProjectStyleLocked ? "到项目总览修改项目画风" : "切换临时画风"}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="p-2 rounded-xl flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                  <Palette size={20} style={{ color: theme.colors.purplePrimary }} />
                </div>
                {isSidebarOpen && (
                  <div className="flex flex-col items-start min-w-0 leading-tight">
                    <span className="text-xs font-bold truncate" style={{ color: 'var(--color-text-muted)' }}>
                      {activeProjectVisualStyleMode === "legacy" ? "临时画风" : "项目画风"}
                    </span>
                    <span className="text-base font-bold truncate" style={{ color: theme.colors.purplePrimary }}>{activeProjectStyleLabel}</span>
                  </div>
                )}
              </div>
              {isSidebarOpen && (
                isProjectStyleLocked
                  ? <ChevronRight size={14} style={{ color: 'var(--color-text-muted)' }} />
                  : <ChevronDown size={14} className={`transition-transform duration-200 ${isStyleMenuOpen ? "rotate-180" : ""}`} style={{ color: 'var(--color-text-muted)' }} />
              )}
            </button>
            {!isProjectStyleLocked && sidebarStyleMenuVisibility.mounted && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setIsStyleMenuOpen(false)} />
                <div className={`absolute top-full mt-2 rounded-xl z-20 overflow-hidden py-1 ${isSidebarOpen ? "left-0 w-full" : "left-full ml-2 w-52 top-0"} ${sidebarStyleMenuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
                  style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
                  {visualStyleOptions.map((style) => {
                    const selected = (activeProjectVisualStyleMode === "none" && style.value === "custom")
                      || (activeProjectVisualStyleMode !== "none" && currentStyle === style.value);
                    return (
                    <button
                      key={style.value}
                      onClick={() => handleStyleChange(style.value)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer"
                      style={{ backgroundColor: selected ? `${theme.colors.purplePrimary}12` : 'transparent' }}
                      onMouseEnter={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; }}
                      onMouseLeave={(e) => { if (!selected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm" style={{ color: selected ? theme.colors.purplePrimary : 'var(--color-text-secondary)' }}>
                          {style.value === "custom" ? "无固定风格" : style.label}
                        </div>
                      </div>
                      {selected && <Check size={14} style={{ color: theme.colors.purplePrimary }} className="flex-shrink-0" />}
                    </button>
                  )})}
                </div>
              </>
            )}
          </div>
        </div>}

        {/* 动态导航菜单 */}
        <div className="relative flex-1 min-h-0 pt-2.5">
          {navScrollState.canScroll && !navScrollState.atTop && (
            <div
              className="pointer-events-none absolute inset-x-0 top-0 z-10 h-8"
              style={{
                background: "linear-gradient(to bottom, var(--color-bg-secondary), transparent)",
              }}
            />
          )}
          <nav
            ref={navScrollRef}
            onScroll={updateNavScrollState}
            className="h-full space-y-2 px-3 overflow-y-auto custom-scrollbar pb-12"
          >
            {isWorkspaceMode ? (
                // --- 工作台模式（在某一集里） ---
                <>
                    {/* 返回导航 */}
                    <div className="flex flex-col gap-1 mb-3 pb-3 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
                      <NavButton onClick={() => navigate("/")} icon={<LayoutGrid size={16} />} label="全部项目" collapsed={!isSidebarOpen} />
                      <NavButton onClick={() => { navigate("/hongguo-rankings"); setIsSidebarOpen(false); }} icon={<TrendingUp size={16} />} label="红果榜单" collapsed={!isSidebarOpen} />
                      <NavButton
                        onClick={() => navigate(narrationProjectActive
                          ? `/project/${workspaceProjectId}/narration-production`
                          : `/project/${workspaceProjectId}`)}
                        icon={<ArrowLeft size={16} />}
                        label={narrationProjectActive ? "解说剧生产" : "项目总览"}
                        collapsed={!isSidebarOpen}
                      />
                    </div>

                    <NavDrawer id="workspaceCurrent" title="当前集" color={theme.colors.accentPrimary} backgroundColor={`${theme.colors.accentPrimary}08`} open={navDrawerOpen.workspaceCurrent !== false} onToggle={toggleNavDrawer}>
                      {can('SCRIPT_IMPORT') && (
                        <NavButton active={currentTab === "upload"} onClick={() => navigate(buildWorkspacePath("upload"))} icon={<BookOpen size={18} />} label="本集剧本" collapsed={!isSidebarOpen} />
                      )}
                    </NavDrawer>

                    <NavDrawer id="workspaceMaking" title="故事制作" color="var(--color-text-secondary)" backgroundColor="var(--color-bg-tertiary)" className="mt-2" open={navDrawerOpen.workspaceMaking !== false} onToggle={toggleNavDrawer}>
                      <NavButton active={currentTab === "mini-storyboard"} onClick={() => navigate(buildWorkspacePath("mini-storyboard"))} icon={<ScrollText size={18} />} label="小故事板" collapsed={!isSidebarOpen} />
                      {!narrationProjectActive && (
                        <>
                          <NavButton active={currentTab === "storyboard-board"} onClick={() => navigate(buildWorkspacePath("storyboard-board"))} icon={<Film size={18} />} label="故事板导演台" collapsed={!isSidebarOpen} />
                          <NavButton active={currentTab === "storyboard"} onClick={() => navigate(buildWorkspacePath("storyboard"))} icon={<Clapperboard size={18} />} label="分镜编辑器" collapsed={!isSidebarOpen} />
                          <NavButton active={currentTab === "canvas"} onClick={() => navigate(buildWorkspacePath("canvas"))} icon={<Workflow size={18} />} label="画布模式" collapsed={!isSidebarOpen} />
                        </>
                      )}
                      <NavButton active={currentTab === "rough-cut"} onClick={() => navigate(buildWorkspacePath("rough-cut"))} icon={<Scissors size={18} />} label="粗剪台" collapsed={!isSidebarOpen} />
                    </NavDrawer>

                    {!narrationProjectActive && (
                      <NavDrawer id="workspaceGenerate" title="生成工具" color="var(--color-purple-primary)" backgroundColor="var(--color-purple-light)" className="mt-2" open={navDrawerOpen.workspaceGenerate !== false} onToggle={toggleNavDrawer}>
                        <NavButton active={currentTab === "imageagent"} onClick={() => navigate(buildWorkspacePath("imageagent"))} icon={<Palette size={18} />} label="图片生成" collapsed={!isSidebarOpen} />
                        <NavButton active={currentTab === "videoagent"} onClick={() => navigate(buildWorkspacePath("videoagent"))} icon={<Film size={18} />} label="视频生成" collapsed={!isSidebarOpen} />
                      </NavDrawer>
                    )}

                    <NavDrawer id="workspaceAssets" title="资产中心" color="var(--color-green-primary)" backgroundColor="var(--color-green-light)" className="mt-2" open={navDrawerOpen.workspaceAssets !== false} onToggle={toggleNavDrawer}>
                      <NavButton active={currentTab === "character"} onClick={() => navigate(buildWorkspacePath("character"))} icon={<Users size={18} />} label="角色工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentTab === "scene"} onClick={() => navigate(buildWorkspacePath("scene"))} icon={<ImageIcon size={18} />} label="场景工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentTab === "prop"} onClick={() => navigate(buildWorkspacePath("prop"))} icon={<Box size={18} />} label="道具工坊" collapsed={!isSidebarOpen} />
                      <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/assets/fight`)} icon={<Swords size={18} />} label="招式工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentTab === "resources"} onClick={() => navigate(buildWorkspacePath("resources"))} icon={<History size={18} />} label="资源中心" collapsed={!isSidebarOpen} />
                    </NavDrawer>

                    {/* 项目工具 */}
                    <div className="my-3 h-px mx-2" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>
                    <NavDrawer id="workspaceTools" title="项目工具" color={theme.colors.accentPrimary} backgroundColor={`${theme.colors.accentPrimary}08`} open={navDrawerOpen.workspaceTools !== false} onToggle={toggleNavDrawer}>
                      {narrationProjectActive && (
                        <NavButton
                          onClick={() => navigate(`/project/${workspaceProjectId}/narration-production`)}
                          icon={<ScrollText size={18} />}
                          label="解说剧生产"
                          collapsed={!isSidebarOpen}
                        />
                      )}
                      <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/script-analysis`)} icon={<FileText size={18} />} label="剧本中心" collapsed={!isSidebarOpen} />
                      <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/music`)} icon={<Music size={18} />} label="音乐音频" collapsed={!isSidebarOpen} />
                      {!narrationProjectActive && (
                        <>
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/story-structure`)} icon={<Workflow size={18} />} label="故事骨架" collapsed={!isSidebarOpen} />
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/adaptation-agent`)} icon={<Bot size={18} />} label="智能改编" collapsed={!isSidebarOpen} />
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/novel-agent`)} icon={<BookOpen size={18} />} label="小说转剧本" collapsed={!isSidebarOpen} />
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/one-click-explainer`)} icon={<Workflow size={18} />} label="一键解说剧" collapsed={!isSidebarOpen} />
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/dialogue-auto`)} icon={<Clapperboard size={18} />} label="对话短剧自动流" collapsed={!isSidebarOpen} />
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/acting-guide`)} icon={<Clapperboard size={18} />} label="AI表演导演表" collapsed={!isSidebarOpen} />
                          <NavButton onClick={() => navigate(`/project/${workspaceProjectId}/reference-video-reverse`)} icon={<Video size={18} />} label="参考视频反拆" collapsed={!isSidebarOpen} />
                        </>
                      )}
                    </NavDrawer>
                </>
            ) : isProjectMode ? (
                // --- 项目级别（项目总览 + 全剧策划 + 资产） ---
                <>
                    <div className="flex flex-col gap-1 mb-3 pb-3 border-b" style={{ borderColor: 'var(--color-border-primary)' }}>
                      <NavButton onClick={() => navigate("/")} icon={<LayoutGrid size={16} />} label="全部项目" collapsed={!isSidebarOpen} />
                      <NavButton onClick={() => { navigate("/hongguo-rankings"); setIsSidebarOpen(false); }} icon={<TrendingUp size={16} />} label="红果榜单" collapsed={!isSidebarOpen} />
                      <NavButton
                        active={interactiveGameProjectActive
                          ? !!interactiveStoryMatch
                          : repaintProjectActive
                            ? !!videoRepaintMatch
                            : narrationProjectActive
                              ? !!narrationProductionMatch
                              : !!projectMatch}
                        onClick={() => navigate(activeProjectEntryPath)}
                        icon={interactiveGameProjectActive
                          ? <Gamepad2 size={18} />
                          : repaintProjectActive
                            ? <Video size={18} />
                            : narrationProjectActive
                              ? <ScrollText size={18} />
                              : <Layers size={18} />}
                        label={interactiveGameProjectActive
                          ? "互动游戏"
                          : repaintProjectActive
                            ? "影视转绘"
                            : narrationProjectActive
                              ? "解说剧生产"
                              : "项目总览"}
                        collapsed={!isSidebarOpen}
                      />
                    </div>

                    <NavDrawer id="projectPlan" title={interactiveGameProjectActive ? "互动游戏" : (repaintProjectActive ? "影视转绘" : "剧本/策划")} color={theme.colors.accentPrimary} backgroundColor={`${theme.colors.accentPrimary}08`} open={navDrawerOpen.projectPlan !== false} onToggle={toggleNavDrawer}>
                      {interactiveGameProjectActive ? (
                        <NavButton active={!!interactiveStoryMatch} onClick={() => navigate(`/project/${currentProjectId}/interactive-story`)} icon={<Gamepad2 size={18} />} label="剧情画布" collapsed={!isSidebarOpen} />
                      ) : repaintProjectActive ? (
                        <NavButton active={!!videoRepaintMatch} onClick={() => navigate(`/project/${currentProjectId}/video-repaint`)} icon={<Video size={18} />} label="影视转绘" collapsed={!isSidebarOpen} />
                      ) : narrationProjectActive ? (
                        <>
                          <NavButton active={!!scriptAnalysisMatch} onClick={() => navigate(`/project/${currentProjectId}/script-analysis`)} icon={<FileText size={18} />} label="剧本中心" collapsed={!isSidebarOpen} />
                          <NavButton active={!!narrationProductionMatch} onClick={() => navigate(`/project/${currentProjectId}/narration-production`)} icon={<ScrollText size={18} />} label="解说剧生产" collapsed={!isSidebarOpen} />
                        </>
                      ) : (
                        <>
                          <NavButton active={!!scriptAnalysisMatch} onClick={() => navigate(`/project/${currentProjectId}/script-analysis`)} icon={<FileText size={18} />} label="剧本中心" collapsed={!isSidebarOpen} />
                          <NavButton active={!!storyStructureMatch} onClick={() => navigate(`/project/${currentProjectId}/story-structure`)} icon={<Workflow size={18} />} label="故事骨架" collapsed={!isSidebarOpen} />
                          <NavButton active={!!adaptationAgentMatch} onClick={() => navigate(`/project/${currentProjectId}/adaptation-agent`)} icon={<Bot size={18} />} label="智能改编" collapsed={!isSidebarOpen} />
                          <NavButton active={!!novelAgentMatch} onClick={() => navigate(`/project/${currentProjectId}/novel-agent`)} icon={<BookOpen size={18} />} label="小说转剧本" collapsed={!isSidebarOpen} />
                          <NavButton active={!!oneClickExplainerMatch} onClick={() => navigate(`/project/${currentProjectId}/one-click-explainer`)} icon={<Workflow size={18} />} label="一键解说剧" collapsed={!isSidebarOpen} />
                          <NavButton active={!!dialogueAutoMatch} onClick={() => navigate(`/project/${currentProjectId}/dialogue-auto`)} icon={<Clapperboard size={18} />} label="对话短剧自动流" collapsed={!isSidebarOpen} />
                        </>
                      )}
                    </NavDrawer>

                    {!repaintProjectActive && !interactiveGameProjectActive && <NavDrawer id="projectGenerate" title="生成工具" color="var(--color-purple-primary)" backgroundColor="var(--color-purple-light)" className="mt-2" open={navDrawerOpen.projectGenerate !== false} onToggle={toggleNavDrawer}>
                      <NavButton active={!!projectMusicMatch} onClick={() => navigate(`/project/${currentProjectId}/music`)} icon={<Music size={18} />} label="音乐音频" collapsed={!isSidebarOpen} />
                      {!narrationProjectActive && (
                        <>
                          <NavButton active={!!actingGuideMatch} onClick={() => navigate(`/project/${currentProjectId}/acting-guide`)} icon={<Clapperboard size={18} />} label="AI表演导演表" collapsed={!isSidebarOpen} />
                          <NavButton active={!!referenceVideoReverseMatch} onClick={() => navigate(`/project/${currentProjectId}/reference-video-reverse`)} icon={<Video size={18} />} label="参考视频反拆" collapsed={!isSidebarOpen} />
                        </>
                      )}
                    </NavDrawer>}

                    {!interactiveGameProjectActive && <NavDrawer id="projectAssets" title="资产中心" color="var(--color-green-primary)" backgroundColor="var(--color-green-light)" className="mt-2" open={navDrawerOpen.projectAssets !== false} onToggle={toggleNavDrawer}>
                      <NavButton active={currentAssetType === "character"} onClick={() => navigate(`/project/${currentProjectId}/assets/character`)} icon={<Users size={18} />} label="角色工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentAssetType === "scene"} onClick={() => navigate(`/project/${currentProjectId}/assets/scene`)} icon={<ImageIcon size={18} />} label="场景工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentAssetType === "prop"} onClick={() => navigate(`/project/${currentProjectId}/assets/prop`)} icon={<Box size={18} />} label="道具工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentAssetType === "fight"} onClick={() => navigate(`/project/${currentProjectId}/assets/fight`)} icon={<Swords size={18} />} label="招式工坊" collapsed={!isSidebarOpen} />
                      <NavButton active={currentAssetType === "image"} onClick={() => navigate(`/project/${currentProjectId}/assets/image`)} icon={<Palette size={18} />} label="图片资源" collapsed={!isSidebarOpen} />
                      <NavButton active={currentAssetType === "resources"} onClick={() => navigate(`/project/${currentProjectId}/assets/resources`)} icon={<History size={18} />} label="资源中心" collapsed={!isSidebarOpen} />
                    </NavDrawer>}
                </>
            ) : (
                // --- 首页 ---
                <>
                    <div className={`rounded-xl px-3 py-2 mb-2 mt-2 transition-opacity duration-300 ${isSidebarOpen ? "opacity-100" : "opacity-0 hidden"}`} style={{ backgroundColor: `${theme.colors.accentPrimary}08` }}>
                      <span className="text-xs font-bold tracking-widest" style={{ color: theme.colors.accentPrimary }}>总览</span>
                    </div>
                    <NavButton active={location.pathname === "/"} onClick={() => navigate("/")} icon={<LayoutGrid size={18} />} label="项目管理" collapsed={!isSidebarOpen} />
                    <NavButton active={location.pathname === "/hongguo-rankings"} onClick={() => { navigate("/hongguo-rankings"); setIsSidebarOpen(false); }} icon={<TrendingUp size={18} />} label="红果榜单" collapsed={!isSidebarOpen} />
                </>
            )}
          </nav>
          {navScrollState.canScroll && !navScrollState.atBottom && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10"
              style={{
                background: "linear-gradient(to top, var(--color-bg-secondary) 16%, transparent)",
              }}
            >
              <div
                className="absolute bottom-1 left-1/2 h-px w-10 -translate-x-1/2 rounded-full"
                style={{
                  backgroundColor: theme.colors.accentPrimary,
                  opacity: 0.45,
                }}
              />
            </div>
          )}
        </div>

        {/* 底部功能区 */}
        <div className="p-3 mt-auto space-y-1.5 border-t shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
             {activeProjectId && !interactiveGameProjectActive && (
               <NavButton
                 onClick={() => handleContextFeatureAction("script-viewer")}
                 icon={<BookOpen size={18} />}
                 label="剧本快览"
                 collapsed={!isSidebarOpen}
                 active={isScriptViewerOpen}
               />
             )}
             <NavButton onClick={() => handleContextFeatureAction("text-assistant")} icon={<MessageSquare size={18} />} label="文字助手" collapsed={!isSidebarOpen} active={isAiChatOpen} />
             {!interactiveGameProjectActive && (
               <NavButton onClick={() => { setIsGenLogOpen(!isGenLogOpen); if (!isGenLogOpen) setIsAiChatOpen(false); }} icon={<ScrollText size={18} />} label="生成日志" collapsed={!isSidebarOpen} active={isGenLogOpen} />
             )}

             {/* 更多（低优先级功能折叠） */}
             <div className="relative">
               <button
                 onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}
                 className={`w-full flex items-center transition-all duration-200 cursor-pointer ${!isSidebarOpen ? "justify-center w-10 h-10 mx-auto" : "gap-3 px-3 py-2.5 w-full"}`}
                 style={{
                   borderRadius: 'var(--radius-md)',
                   border: '1px solid transparent',
                   color: isMoreMenuOpen ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                 }}
                 onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.borderColor = 'var(--color-border-primary)'; }}
                 onMouseLeave={(e) => { if (!isMoreMenuOpen) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; } }}
                 title={!isSidebarOpen ? "更多" : ""}
               >
                 <MoreHorizontal size={18} className="flex-shrink-0" />
                 <span className={`font-medium whitespace-nowrap overflow-hidden transition-all duration-300 text-sm ${!isSidebarOpen ? "w-0 opacity-0" : "w-auto opacity-100"}`}>更多</span>
                 {isSidebarOpen && <ChevronDown size={14} className={`ml-auto flex-shrink-0 transition-transform duration-200 ${isMoreMenuOpen ? "rotate-180" : ""}`} />}
               </button>
               {moreMenuVisibility.mounted && (
                 <>
                   <div className="fixed inset-0 z-10" onClick={() => setIsMoreMenuOpen(false)} />
                   <div className={`absolute z-20 ${!isSidebarOpen ? "left-full bottom-0 ml-2 w-44" : "bottom-full left-0 mb-2 w-full"} rounded-lg shadow-lg overflow-hidden py-1 ${moreMenuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
                     style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                     {isWorkspaceMode && (
                       <>
                         <button onClick={() => { navigate(buildWorkspacePath("education")); setIsMoreMenuOpen(false); }}
                           className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                           style={{ color: currentTab === "education" ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                           <BookOpen size={16} className="flex-shrink-0" /><span className="text-sm">教学中心</span>
                         </button>
                         <button onClick={() => { navigate(buildWorkspacePath("resources")); setIsMoreMenuOpen(false); }}
                           className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                           style={{ color: currentTab === "resources" ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                           <History size={16} className="flex-shrink-0" /><span className="text-sm">资源中心</span>
                         </button>
                         <button onClick={() => { navigate(buildWorkspacePath("motion-storyboard")); setIsMoreMenuOpen(false); }}
                           className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                           style={{ color: currentTab === "motion-storyboard" ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                           <Film size={16} className="flex-shrink-0" /><span className="text-sm">动态分镜</span>
                         </button>
                         <button onClick={() => { navigate(buildWorkspacePath("orbit")); setIsMoreMenuOpen(false); }}
                           className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                           style={{ color: currentTab === "orbit" ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                           <Rotate3D size={16} className="flex-shrink-0" /><span className="text-sm">3D 视角</span>
                         </button>
                         <div className="my-1 h-px mx-2" style={{ backgroundColor: 'var(--color-border-primary)' }} />
                       </>
                     )}
                     <button onClick={() => { navigate("/help"); setIsMoreMenuOpen(false); }}
                       className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                       style={{ color: location.pathname === '/help' ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                       <BookOpen size={16} className="flex-shrink-0" /><span className="text-sm">使用指南</span>
                     </button>
                     <button onClick={() => { navigate("/ai-ownership-agreement"); setIsMoreMenuOpen(false); }}
                       className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                       style={{ color: location.pathname === '/ai-ownership-agreement' ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                       <FileText size={16} className="flex-shrink-0" /><span className="text-sm">权属说明</span>
                     </button>
                     <button onClick={() => { navigate("/gallery"); setIsMoreMenuOpen(false); }}
                       className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                       style={{ color: location.pathname === '/gallery' ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                       <Film size={16} className="flex-shrink-0" /><span className="text-sm">作品展廊</span>
                     </button>
                     <button onClick={() => { toggleSnow(); setIsMoreMenuOpen(false); }}
                       className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                       style={{ color: isSnowing ? theme.colors.info : 'var(--color-text-secondary)' }}>
                       <Snowflake size={16} className={`flex-shrink-0 ${isSnowing ? "animate-spin-slow" : ""}`} /><span className="text-sm">{isSnowing ? "停止下雪" : "下雪特效"}</span>
                     </button>
                     {canAccessOperations && (
                       <button onClick={() => { navigate("/operations"); setIsMoreMenuOpen(false); }}
                         className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                         style={{ color: 'var(--color-text-secondary)' }}>
                         <TrendingUp size={16} className="flex-shrink-0" /><span className="text-sm">运营管理</span>
                       </button>
                     )}
                     {isAdmin && (
                       <>
                       <button onClick={() => { navigate("/admin/assets"); setIsMoreMenuOpen(false); }}
                         className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                         style={{ color: location.pathname === '/admin/assets' ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                         <Film size={16} className="flex-shrink-0" /><span className="text-sm">素材库管理</span>
                       </button>
                       <button onClick={() => { navigate("/admin"); setIsMoreMenuOpen(false); }}
                         className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                         style={{ color: location.pathname === '/admin' ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                         <LayoutDashboard size={16} className="flex-shrink-0" /><span className="text-sm">管理后台</span>
                       </button>
                       <button onClick={() => { navigate("/admin?tab=users"); setIsMoreMenuOpen(false); }}
                         className="w-full flex items-center gap-3 px-3 py-2.5 text-left transition-all duration-200 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
                         style={{ color: location.pathname === '/admin/users' || (location.pathname === '/admin' && location.search.includes('tab=users')) ? 'var(--color-accent-primary)' : 'var(--color-text-secondary)' }}>
                         <Users size={16} className="flex-shrink-0" /><span className="text-sm">用户管理</span>
                       </button>
                       </>
                     )}
                   </div>
                 </>
               )}
             </div>

             <button onClick={handleSwitchUser} className={`w-full flex items-center transition-all duration-200 cursor-pointer group/user ${isSidebarOpen ? "p-2.5 gap-3" : "p-2 justify-center aspect-square"}`}
                style={{
                  borderRadius: 'var(--radius-md)',
                  backgroundColor: 'var(--color-bg-tertiary)',
                  border: '1px solid var(--color-border-primary)'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-hover)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; e.currentTarget.style.boxShadow = 'none'; }}>
                <div data-no-translate className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg text-xs font-bold" style={{ backgroundColor: 'var(--color-accent-primary)', border: '1px solid var(--color-border-primary)', color: 'white' }}>{userId ? displayUser.substring(0, 2).toUpperCase() : "U"}</div>
                {isSidebarOpen && (
                    <div className="flex-1 min-w-0 text-left">
                        <div className="flex justify-between items-center"><div data-no-translate className="text-sm font-bold whitespace-nowrap truncate max-w-[110px]" style={{ color: 'var(--color-text-primary)' }}>{displayUser}</div><LogOut size={13} className="transition-all duration-200" style={{ color: 'var(--color-text-muted)' }}/></div>
                        <div className="text-xs whitespace-nowrap" style={{ color: 'var(--color-text-muted)' }}>点击退出登录</div>
                    </div>
                )}
             </button>
             {isSidebarOpen && (
               <a
                 href="https://beian.miit.gov.cn/"
                 target="_blank"
                 rel="noreferrer"
                 className="block pt-1.5 text-center text-xs transition-colors hover:text-[var(--color-text-secondary)]"
                 style={{ color: 'var(--color-text-muted)' }}
               >
                 蜀ICP备2026031582号
               </a>
             )}
        </div>
      </div>

      {/* === 右侧主内容区 (Outlet) === */}
      <div className="flex-1 w-full h-full relative overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        {/* 非项目页导航入口；项目页入口在顶部项目栏内 */}
        {!activeProjectId && !isSidebarOpen && (
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="fixed top-3 left-3 z-30 flex h-10 w-12 items-center justify-center rounded-xl shadow-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
            aria-label="打开菜单"
            title="打开菜单"
          >
            <PanelLeftOpen size={19} />
          </button>
        )}
        {activeProjectId && (
          <ProjectContextBar
            projectId={activeProjectId}
            project={activeProject}
            projectName={activeProjectName}
            isWorkspaceMode={isWorkspaceMode}
            episodeId={activeEpisodeId}
            episodeLabel={activeEpisodeLabel}
            episodes={projectEpisodes}
            currentViewLabel={currentViewLabel}
            featureGroups={contextFeatureGroups}
            isInteractiveGame={interactiveGameProjectActive}
            videoRatio={activeProjectVideoRatio}
            activeModel={activeModel}
            activeModelDisplayName={activeModelDisplayName}
            activeModelColor={activeModelColor}
            modelOptions={MODEL_MENU_PROVIDERS}
            currentProvider={currentProvider}
            dreamflyLlmModel={dreamflyLlmModel}
            dreamflyLlmModelOptions={dreamflyLlmCatalog.models}
            dreamflyLlmModelLoading={dreamflyLlmCatalog.loading}
            dreamflyLlmModelError={dreamflyLlmCatalog.error}
            styleLabel={activeProjectStyleLabel}
            canEditProjectType={canEditProjectType}
            onToggleNavigation={() => setIsSidebarOpen(true)}
            onProjectTypeClick={() => setIsProjectTypeSettingsOpen(true)}
            onProviderChange={handleProviderChange}
            onDreamflyLlmModelChange={handleDreamflyLlmModelChange}
            onStyleClick={() => {
              setIsSidebarOpen(true);
              if (isProjectStyleLocked) {
                handleStylePanelClick();
              } else {
                setIsStyleMenuOpen(true);
              }
            }}
            onGoHome={() => navigate("/")}
            onGoProject={() => navigate(activeProjectEntryPath)}
            onFeatureNavigate={(path) => navigate(path)}
            onFeatureAction={handleContextFeatureAction}
            onEpisodeChange={handleEpisodeSwitch}
            onVideoRatioChange={handleProjectVideoRatioChange}
            pointSummary={currentPointSummary}
            quickTools={!interactiveGameProjectActive ? (
              <ProjectQuickAccessDock
                key={`${activeProjectId}:${activeEpisodeId}:${currentViewLabel}`}
                scriptOpen={isScriptViewerOpen}
                assistantOpen={isAiChatOpen}
                onToggleScript={() => handleContextFeatureAction("script-viewer")}
                onToggleAssistant={() => handleContextFeatureAction("text-assistant")}
                guideOpen={creationGuide?.open && creationGuide.mode !== 'optimize'}
                optimizerOpen={creationGuide?.open && creationGuide.mode === 'optimize'}
                onOpenGuide={() => handleContextFeatureAction("creation-guide")}
                onOpenOptimizer={() => handleContextFeatureAction("prompt-optimizer")}
                accentColor={theme.colors.accentPrimary}
              />
            ) : null}
          />
        )}
        {!activeProjectId && (
          <div
            ref={floatingPointBalanceRef}
            className="fixed right-4 top-3 z-20 h-9 shadow-lg"
          >
            <UserPointBalance
              summary={currentPointSummary}
              accentColor={theme.colors.accentPrimary}
              className="h-full"
            />
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-hidden">
          {/* Keep the routed workspace mounted across sibling navigation. In particular,
              ProjectAssetsWrapper retains in-flight character/scene/prop generation state. */}
          <div className="h-full min-h-0 overflow-hidden ui-page-enter">
            <Outlet />
          </div>
        </div>
      </div>

      {creationGuide && activeProjectId && !interactiveGameProjectActive && (
        <Suspense fallback={<div role="status" className="fixed bottom-6 right-6 z-[106] rounded-lg border px-4 py-3 text-xs" style={{ background: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border-primary)' }}>正在加载创作工具箱…</div>}>
          <CreationGuidePanel
            key={creationGuideScope}
            isOpen={creationGuide.open}
            mode={creationGuide.mode}
            onModeChange={(mode) => setCreationGuide((current) => ({ ...current, mode }))}
            onClose={() => setCreationGuide((current) => ({ ...current, open: false }))}
            projectId={activeProjectId}
            provider={currentProvider}
            model={currentProvider === DREAMFLY_LLM_PROVIDER_ID ? dreamflyLlmModel : ''}
            modelLabel={activeModelDisplayName}
            canOptimize={can('STORYBOARD_EDIT')}
          />
        </Suspense>
      )}

      {/* 文字助手抽屉面板 */}
      <AiChatPanel
        isOpen={isAiChatOpen}
        onClose={() => setIsAiChatOpen(false)}
        projectId={activeProjectId}
        episodeId={activeEpisodeId}
        episodes={projectEpisodes}
        aspectRatio={activeProjectVideoRatio}
      />

      {/* 生成日志抽屉面板 */}
      {!interactiveGameProjectActive && (
        <GenerationLogPanel isOpen={isGenLogOpen} onClose={() => setIsGenLogOpen(false)} projectId={activeProjectId} />
      )}

      {/* 全局剧本快览 */}
      {!interactiveGameProjectActive && (
        <GlobalScriptViewer
          isOpen={isScriptViewerOpen}
          onClose={() => setIsScriptViewerOpen(false)}
          projectId={activeProjectId}
          currentEpisodeId={activeEpisodeId}
        />
      )}

      {isProjectTypeSettingsOpen && activeProject && canEditProjectType && (
        <ProjectTypeSettingsModal
          project={activeProject}
          onClose={() => setIsProjectTypeSettingsOpen(false)}
          onSave={handleProjectTypeChange}
        />
      )}

      {/* 切换用户弹窗 */}
      {switchUserVisibility.mounted && (
        <div
          className={`fixed inset-0 flex items-center justify-center z-[60] ${switchUserVisibility.exiting ? "ui-backdrop-exit" : "ui-backdrop-enter"}`}
          style={{ backgroundColor: 'var(--overlay-light)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
          onMouseDown={() => setIsSwitchUserOpen(false)}
        >
          <div
            className={`relative rounded-2xl w-[420px] shadow-2xl overflow-hidden ${switchUserVisibility.exiting ? "ui-modal-exit" : "ui-modal-enter"}`}
            style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>切换工作区</h2>
                <button onClick={() => setIsSwitchUserOpen(false)} className="ui-pressable p-2 rounded-full cursor-pointer"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}>
                  <X size={18}/>
                </button>
              </div>
              <div>
                <label className="block text-xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>用户名</label>
                <input
                  className="w-full rounded-lg px-4 py-3 outline-none transition-all duration-200"
                  style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                  placeholder="请输入用户名..."
                  value={switchUserInput}
                  onChange={(e) => { setSwitchUserInput(e.target.value); setNeedPassword(true); setPasswordError(""); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSwitchUserConfirm()}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--color-accent-primary)'; e.currentTarget.style.boxShadow = '0 0 0 2px var(--color-accent-glow)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; e.currentTarget.style.boxShadow = 'none'; }}
                  autoFocus
                />
              </div>
              {needPassword && (
                <div className="mt-4">
                  <label className="block text-xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>登录密码</label>
                  <input
                    type="password"
                    className="w-full rounded-lg px-4 py-3 outline-none transition-all duration-200"
                    style={{ backgroundColor: 'var(--color-bg-overlay)', border: `1px solid ${passwordError ? 'var(--color-error)' : 'var(--color-border-primary)'}`, color: 'var(--color-text-primary)' }}
                    placeholder="请输入该用户的登录密码..."
                    value={adminPasswordInput}
                    onChange={(e) => { setAdminPasswordInput(e.target.value); setPasswordError(""); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSwitchUserConfirm()}
                    autoFocus
                  />
                  {passwordError && <p className="text-xs mt-1.5" style={{ color: 'var(--color-error)' }}>{passwordError}</p>}
                </div>
              )}
              <div className="flex justify-end gap-3 mt-8">
                <button onClick={() => setIsSwitchUserOpen(false)} className="ui-pressable px-5 py-2.5 text-sm rounded-lg cursor-pointer" style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.color = 'var(--color-text-primary)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}>
                  取消
                </button>
                <button onClick={handleSwitchUserConfirm} className="ui-pressable px-6 py-2.5 rounded-lg font-bold text-sm cursor-pointer" style={{ backgroundColor: 'var(--color-accent-primary)', color: 'white' }}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px var(--color-accent-glow)'; e.currentTarget.style.filter = 'brightness(1.1)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.filter = 'none'; }}>
                  确认
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProjectContextBar = React.memo(({
  projectId,
  project,
  projectName,
  isWorkspaceMode,
  episodeId,
  episodeLabel,
  episodes,
  currentViewLabel,
  featureGroups = [],
  isInteractiveGame = false,
  videoRatio,
  activeModel,
  activeModelDisplayName,
  activeModelColor,
  modelOptions = [],
  currentProvider,
  dreamflyLlmModel = "",
  dreamflyLlmModelOptions = [],
  dreamflyLlmModelLoading = false,
  dreamflyLlmModelError = "",
  styleLabel,
  canEditProjectType = false,
  onToggleNavigation,
  onProjectTypeClick,
  onProviderChange,
  onDreamflyLlmModelChange,
  onStyleClick,
  onGoHome,
  onGoProject,
  onFeatureNavigate,
  onFeatureAction,
  onEpisodeChange,
  onVideoRatioChange,
  pointSummary,
  quickTools,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const market = projectMarketMeta(project);
  const ActiveModelIcon = activeModel?.icon;
  const [isToolbarModelMenuOpen, setIsToolbarModelMenuOpen] = useState(false);
  const [isFeatureMenuOpen, setIsFeatureMenuOpen] = useState(false);
  const featureButtonRef = useRef(null);
  const [featureMenuPosition, setFeatureMenuPosition] = useState({ top: 52, left: 16 });
  const hasFeatureGroups = useMemo(
    () => featureGroups.some((group) => Array.isArray(group.items) && group.items.length > 0),
    [featureGroups]
  );
  const currentFeature = useMemo(
    () => featureGroups.flatMap((group) => group.items || []).find((item) => item.active),
    [featureGroups]
  );
  const featureMenuVisibility = useDelayedUnmount(
    Boolean(isFeatureMenuOpen && hasFeatureGroups),
    110
  );
  const modelMenuVisibility = useDelayedUnmount(isToolbarModelMenuOpen, 110);
  const CurrentFeatureIcon = currentFeature?.icon;

  // 路由/剧集切换时在渲染期同步关闭功能菜单（render-adjust 模式，替代 effect 内 setState）
  const featureMenuKey = `${currentViewLabel}|${episodeId}|${projectId}`;
  const [prevFeatureMenuKey, setPrevFeatureMenuKey] = useState(featureMenuKey);
  if (prevFeatureMenuKey !== featureMenuKey) {
    setPrevFeatureMenuKey(featureMenuKey);
    setIsFeatureMenuOpen(false);
  }

  const updateFeatureMenuPosition = useCallback(() => {
    const node = featureButtonRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const menuWidth = 320;
    const viewportPadding = 12;
    setFeatureMenuPosition({
      top: Math.min(rect.bottom + 8, window.innerHeight - viewportPadding),
      left: Math.max(
        viewportPadding,
        Math.min(rect.left, window.innerWidth - menuWidth - viewportPadding)
      ),
    });
  }, []);

  useEffect(() => {
    if (!isFeatureMenuOpen) return undefined;
    updateFeatureMenuPosition();
    window.addEventListener("resize", updateFeatureMenuPosition);
    window.addEventListener("scroll", updateFeatureMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateFeatureMenuPosition);
      window.removeEventListener("scroll", updateFeatureMenuPosition, true);
    };
  }, [isFeatureMenuOpen, updateFeatureMenuPosition]);

  return (
    <div
      className="h-12 shrink-0 px-3 md:px-4 border-b flex items-center justify-between gap-3"
      style={{
        borderColor: "var(--color-border-primary)",
        backgroundColor: "var(--color-bg-secondary)",
      }}
    >
      <div className="min-w-0 flex items-center gap-1.5">
        <button
          type="button"
          onClick={onToggleNavigation}
          className="mr-1 flex h-9 w-11 shrink-0 items-center justify-center rounded-xl border transition-colors hover:bg-[var(--color-bg-tertiary)]"
          style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
          aria-label="打开导航"
          title="打开导航"
        >
          <PanelLeftOpen size={18} />
        </button>
        <div className="min-w-0 flex items-center gap-1.5 text-xs">
        <button
          type="button"
          onClick={onGoHome}
          className="h-8 px-2 rounded-md inline-flex items-center gap-1.5 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
          style={{ color: "var(--color-text-muted)" }}
          title="回到全部项目"
        >
          <LayoutGrid size={14} />
          <span className="hidden sm:inline">全部项目</span>
        </button>
        <ChevronRight size={14} style={{ color: "var(--color-text-muted)" }} />
        <button
          type="button"
          onClick={onGoProject}
          className="h-8 max-w-[220px] px-2 rounded-md inline-flex items-center gap-1.5 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
          style={{ color: "var(--color-text-secondary)" }}
          title={projectName || `项目 ${projectId}`}
        >
          {isInteractiveGame ? <Gamepad2 size={14} /> : <Folder size={14} />}
          <span className="truncate">{projectName || `项目 ${projectId}`}</span>
        </button>
        {canEditProjectType ? (
          <button
            type="button"
            onClick={onProjectTypeClick}
            className="group hidden h-11 cursor-pointer items-center rounded-lg px-1.5 transition-colors duration-200 hover:bg-[var(--color-bg-tertiary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent-primary)] md:inline-flex"
            title={`${market.label}，默认视频供应商：${market.provider}；点击修改项目类型`}
            aria-label={`修改项目类型，当前为${market.label}，${market.provider}`}
            aria-haspopup="dialog"
          >
            <span
              className="inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-2xs font-bold"
              style={{
                color: market.color,
                borderColor: market.colorBorder,
                backgroundColor: market.colorSoft,
              }}
            >
              {market.label}
              <span style={{ color: "var(--color-text-muted)" }}>{market.provider}</span>
              <ChevronDown size={12} className="transition-transform duration-200 group-hover:translate-y-0.5" />
            </span>
          </button>
        ) : (
          <span
            className="hidden h-7 items-center gap-1.5 rounded-md border px-2 text-2xs font-bold md:inline-flex"
            style={{
              color: market.color,
              borderColor: market.colorBorder,
              backgroundColor: market.colorSoft,
            }}
            title={`${market.label}，默认视频供应商：${market.provider}`}
          >
            {market.label}
            <span style={{ color: "var(--color-text-muted)" }}>{market.provider}</span>
          </span>
        )}
        {isWorkspaceMode && (
          <>
            <ChevronRight size={14} style={{ color: "var(--color-text-muted)" }} />
            <span
              className="h-8 max-w-[240px] px-2 rounded-md inline-flex items-center gap-1.5"
              style={{ color: theme.colors.accentPrimary, backgroundColor: `${theme.colors.accentPrimary}12` }}
              title={episodeLabel}
            >
              <BookOpen size={14} />
              <span className="truncate">{episodeLabel}</span>
            </span>
          </>
        )}
        <ChevronRight size={14} style={{ color: "var(--color-text-muted)" }} />
        <div className="relative min-w-0">
          <button
            ref={featureButtonRef}
            type="button"
            onClick={() => hasFeatureGroups && setIsFeatureMenuOpen((open) => !open)}
            className="h-8 min-w-0 max-w-[190px] px-2 rounded-md inline-flex items-center gap-1.5 font-semibold truncate transition-colors hover:bg-[var(--color-bg-tertiary)] disabled:opacity-80"
            style={{
              color: "var(--color-text-primary)",
              backgroundColor: isFeatureMenuOpen ? "var(--color-bg-tertiary)" : "transparent",
            }}
            disabled={!hasFeatureGroups}
            title="切换功能"
            aria-haspopup="true"
            aria-expanded={isFeatureMenuOpen}
          >
            {CurrentFeatureIcon && <CurrentFeatureIcon size={14} className="shrink-0" style={{ color: theme.colors.accentPrimary }} />}
            <span className="truncate">{currentViewLabel}</span>
            {hasFeatureGroups && <ChevronDown size={13} className={`shrink-0 transition-transform ${isFeatureMenuOpen ? "rotate-180" : ""}`} />}
          </button>
          {featureMenuVisibility.mounted && createPortal(
            <>
              <div className="fixed inset-0 z-30" onClick={() => setIsFeatureMenuOpen(false)} />
              <div
                className={`fixed z-40 w-[320px] max-w-[calc(100vw-24px)] overflow-hidden rounded-xl p-2 shadow-2xl ${featureMenuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
                style={{
                  top: featureMenuPosition.top,
                  left: featureMenuPosition.left,
                  backgroundColor: "var(--color-bg-secondary)",
                  border: "1px solid var(--color-border-primary)",
                }}
              >
                <div className="max-h-[70vh] overflow-y-auto pr-1 custom-scrollbar">
                  {featureGroups.map((group) => (
                    <div key={group.title} className="mb-2 last:mb-0">
                      <div className="px-2 py-1 text-[10px] font-bold" style={{ color: "var(--color-text-muted)" }}>
                        {group.title}
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {(group.items || []).map((item) => {
                          const ItemIcon = item.icon;
                          const selected = Boolean(item.active || item.selected);
                          return (
                            <button
                              key={item.key}
                              type="button"
                              onClick={() => {
                                if (item.action) onFeatureAction?.(item.action);
                                else if (!item.active && item.path) onFeatureNavigate?.(item.path);
                                setIsFeatureMenuOpen(false);
                              }}
                              className="ui-pressable flex min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-xs hover:bg-[var(--color-bg-tertiary)]"
                              style={{
                                color: selected ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                                backgroundColor: selected ? `${theme.colors.accentPrimary}16` : "transparent",
                                border: selected ? `1px solid ${theme.colors.accentPrimary}40` : "1px solid transparent",
                              }}
                            >
                              {ItemIcon && <ItemIcon size={15} className="shrink-0" />}
                              <span className="truncate">{item.label}</span>
                              {selected && <Check size={13} className="ml-auto shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>,
            document.body
          )}
        </div>
        </div>
      </div>

      <div className="shrink-0 flex items-center gap-2">
        {quickTools}
        {!isInteractiveGame && activeModel && (
          <div className="relative hidden xl:block">
            <button
              type="button"
              onClick={() => setIsToolbarModelMenuOpen((open) => !open)}
              className="inline-flex h-8 max-w-[210px] items-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition-colors hover:bg-[var(--color-bg-tertiary)]"
              style={{
                borderColor: `${activeModelColor || theme.colors.accentPrimary}55`,
                color: "var(--color-text-secondary)",
                backgroundColor: `${activeModelColor || theme.colors.accentPrimary}0f`,
              }}
              title="切换AI模型"
              aria-expanded={isToolbarModelMenuOpen}
              aria-haspopup="true"
            >
              {ActiveModelIcon && <ActiveModelIcon size={14} style={{ color: activeModelColor || theme.colors.accentPrimary }} />}
              <span className="truncate">{activeModelDisplayName || activeModel.name}</span>
              <ChevronDown size={13} className={`shrink-0 transition-transform ${isToolbarModelMenuOpen ? "rotate-180" : ""}`} />
            </button>
            {modelMenuVisibility.mounted && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setIsToolbarModelMenuOpen(false)} />
                <div
                  className={`absolute right-0 top-full z-40 mt-2 max-h-[70vh] w-72 overflow-y-auto rounded-xl p-1.5 shadow-2xl ${modelMenuVisibility.exiting ? "ui-popover-exit" : "ui-popover-enter"}`}
                  style={{
                    backgroundColor: "var(--color-bg-secondary)",
                    border: "1px solid var(--color-border-primary)",
                  }}
                >
                  {modelOptions.map((provider) => {
                    const ProviderIcon = provider.icon;
                    const selected = provider.id === currentProvider;
                    const providerColor = provider.color || theme.colors.accentPrimary;
                    return (
                      <button
                        key={provider.id}
                        type="button"
                        onClick={() => {
                          onProviderChange?.(provider.id);
                          if (provider.id !== DREAMFLY_LLM_PROVIDER_ID) {
                            setIsToolbarModelMenuOpen(false);
                          }
                        }}
                        className="ui-pressable flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left hover:bg-[var(--color-bg-tertiary)]"
                        style={{
                          backgroundColor: selected ? `${providerColor}18` : "transparent",
                          color: selected ? providerColor : "var(--color-text-secondary)",
                        }}
                      >
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                          style={{
                            backgroundColor: `${providerColor}18`,
                            border: `1px solid ${providerColor}35`,
                          }}
                        >
                          {ProviderIcon && <ProviderIcon size={15} style={{ color: providerColor }} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-xs font-bold">{provider.name}</span>
                          {provider.recommendation && (
                            <span className="block truncate text-[10px]" style={{ color: "var(--color-text-muted)" }}>
                              {provider.recommendation}
                            </span>
                          )}
                        </span>
                        {selected && <Check size={14} className="shrink-0" style={{ color: providerColor }} />}
                      </button>
                    );
                  })}
                  {currentProvider === DREAMFLY_LLM_PROVIDER_ID && (
                    <div
                      className="mt-1 border-t px-2 pb-1 pt-2"
                      style={{ borderColor: "var(--color-border-primary)" }}
                    >
                      <div className="mb-1 text-[10px] font-bold" style={{ color: "var(--color-text-muted)" }}>
                        Dreamfly 子模型
                      </div>
                      <DreamflyLlmModelSelect
                        value={dreamflyLlmModel}
                        options={dreamflyLlmModelOptions}
                        loading={dreamflyLlmModelLoading}
                        error={dreamflyLlmModelError}
                        onChange={onDreamflyLlmModelChange}
                      />
                      {dreamflyLlmModelError && (
                        <p className="mt-1 text-[10px] leading-4" style={{ color: "var(--color-warning)" }}>
                          无法加载模型目录，保留已有选择；未选时不可调用
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}
        {!isInteractiveGame && styleLabel && (
          <button
            type="button"
            onClick={onStyleClick}
            className="hidden xl:inline-flex h-8 max-w-[190px] items-center gap-1.5 rounded-md border px-2 text-xs font-semibold transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{
              borderColor: `${theme.colors.purplePrimary || theme.colors.accentPrimary}55`,
              color: "var(--color-text-secondary)",
              backgroundColor: `${theme.colors.purplePrimary || theme.colors.accentPrimary}10`,
            }}
            title="项目画风"
          >
            <Palette size={14} style={{ color: theme.colors.purplePrimary || theme.colors.accentPrimary }} />
            <span className="truncate">{styleLabel}</span>
          </button>
        )}
        {!isInteractiveGame && <label className="hidden lg:flex items-center gap-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
          视频画幅
          <AppSelect
            value={normalizeProjectVideoRatio(videoRatio)}
            onChange={(event) => onVideoRatioChange?.(event.target.value)}
            className="h-8 rounded-md border px-2 text-xs outline-none cursor-pointer"
            style={{
              backgroundColor: "var(--color-bg-primary)",
              borderColor: "var(--color-border-primary)",
              color: "var(--color-text-primary)",
            }}
            title="项目默认视频画幅"
          >
            {PROJECT_VIDEO_RATIOS.map((ratio) => (
              <option key={ratio} value={ratio}>{ratio}</option>
            ))}
          </AppSelect>
        </label>}
        {isWorkspaceMode && (
          <label className="hidden md:flex items-center gap-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
            当前集
            <AppSelect
              value={episodeId || ""}
              onChange={(event) => onEpisodeChange?.(event.target.value)}
              className="h-8 max-w-[220px] rounded-md border px-2 text-xs outline-none cursor-pointer"
              style={{
                backgroundColor: "var(--color-bg-primary)",
                borderColor: "var(--color-border-primary)",
                color: "var(--color-text-primary)",
              }}
            >
              {episodeId && !episodes.some((episode) => String(episode.id) === String(episodeId)) && (
                <option value={episodeId}>{episodeLabel}</option>
              )}
              {episodes.map((episode, index) => (
                <option key={episode.id} value={episode.id}>
                  {normalizeEpisodeLabel(episode, index + 1)}
                </option>
              ))}
            </AppSelect>
          </label>
        )}
        <button
          type="button"
          onClick={onGoProject}
          className="h-8 px-3 rounded-md border text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer hover:bg-[var(--color-bg-tertiary)]"
          style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)" }}
          title={isInteractiveGame ? "回到互动游戏" : "回到项目总览"}
        >
          {isInteractiveGame ? <Gamepad2 size={14} /> : <Layers size={14} />}
          <span className="hidden sm:inline">{isInteractiveGame ? "互动游戏" : "项目总览"}</span>
        </button>
        <UserPointBalance
          summary={pointSummary}
          accentColor={theme.colors.accentPrimary}
          className="h-8"
        />
      </div>
    </div>
  );
});

// 页面包装器组件
const DashboardWrapper = () => {
  const { userId } = useUser();
  return <ProjectDashboard userId={userId} />;
};

// 首页模式开关：像素小镇 / 项目列表（读 localStorage home_mode，无记录默认 list，老用户无感）
const HomeModeSwitch = () => (
  localStorage.getItem("home_mode") === "pixel" ? <PixelOfficePage /> : <DashboardWrapper />
);

const EpisodeWrapper = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useUser();
  const [project, setProject] = useState(null);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const list = await authenticatedFetch(`${API_HOST}/api/project/list`, { headers: { "X-User-ID": userId } });
        if (Array.isArray(list)) {
          const found = list.find(p => String(p.id) === projectId);
          if (found) setProject(found);
          else navigate("/");
        }
      } catch (e) { console.error(e); }
    };
    if (userId) fetchProject();
  }, [projectId, userId, navigate]);

  if (!project) return <div className="flex h-screen items-center justify-center text-[var(--color-text-muted)]">加载项目信息...</div>;
  const projectRootPath = `/project/${projectId}`;
  const intendedEntryPath = getProjectEntryPath(project, location.state?.projectProductionMode);
  if (intendedEntryPath !== projectRootPath) {
    return <Navigate to={intendedEntryPath} replace />;
  }

  return (
    <ProjectOnboardingGate project={project}>
      <EpisodeSelector project={project} onProjectUpdated={setProject} onBack={() => navigate("/")} onSelectEpisode={(ep) => navigate(`/project/${projectId}/episode/${ep.id}/mini-storyboard`)} />
    </ProjectOnboardingGate>
  );
};

const AdaptationAgentWrapper = () => {
  const { projectId } = useParams();
  return <AdaptationAgentWorkbench projectId={projectId} />;
};

const NovelAgentWrapper = ({ autoMode = "" }) => {
  const { projectId } = useParams();
  return <AdaptationAgentWorkbench projectId={projectId} mode="novel" autoMode={autoMode} />;
};

const EpisodeProductionWrapper = () => {
  const { projectId } = useParams();
  return <EpisodeProductionWorkbench projectId={projectId} />;
};

const ProjectMusicWrapper = () => {
  const { projectId } = useParams();
  return <MusicAgentModern projectId={projectId} />;
};

const StoryStructureWrapper = () => {
  const { projectId } = useParams();
  return <StoryStructureWorkbench projectId={projectId} />;
};

// 项目级画布：一个项目一张画布（episodeId 留空，NodeCanvas 内部按可选处理）
const ProjectCanvasWrapper = () => {
  const { projectId } = useParams();
  return (
    <div className="w-full h-full relative overflow-hidden">
      <CanvasMode projectId={projectId} />
    </div>
  );
};

// 项目级别资产管理 — 统一包装组件，切换资产类型不卸载子组件
const ProjectAssetsWrapper = () => {
  const { projectId, assetType } = useParams();
  const activeType = assetType || "character";
  const [visitedTypes, setVisitedTypes] = useState(() => new Set([activeType]));

  // 渲染期同步记录已访问的资产类型（render-adjust 模式，替代 effect 内 setState）
  if (!visitedTypes.has(activeType)) {
    setVisitedTypes(prev => {
      if (prev.has(activeType)) return prev;
      const next = new Set(prev);
      next.add(activeType);
      return next;
    });
  }

  return (
    <div className="w-full h-full relative overflow-hidden">
      {visitedTypes.has("character") && (
        <div className={activeType === "character" ? "w-full h-full overflow-hidden" : "hidden"}>
          <CharacterGenerator projectId={projectId} />
        </div>
      )}
      {visitedTypes.has("scene") && (
        <div className={activeType === "scene" ? "w-full h-full overflow-hidden" : "hidden"}>
          <SceneGenerator projectId={projectId} />
        </div>
      )}
      {visitedTypes.has("prop") && (
        <div className={activeType === "prop" ? "w-full h-full overflow-hidden" : "hidden"}>
          <PropGenerator projectId={projectId} />
        </div>
      )}
      {visitedTypes.has("fight") && (
        <div className={activeType === "fight" ? "w-full h-full overflow-hidden" : "hidden"}>
          <FightActionWorkshop projectId={projectId} />
        </div>
      )}
      {visitedTypes.has("image") && (
        <div className={activeType === "image" ? "w-full h-full overflow-hidden" : "hidden"}>
          <ImageAgentModern projectId={projectId} />
        </div>
      )}
      {visitedTypes.has("resources") && (
        <div className={activeType === "resources" ? "w-full h-full overflow-hidden" : "hidden"}>
          <ResourceCenter projectId={projectId} />
        </div>
      )}
    </div>
  );
};

/**
 * ✅ 3. 工作台内容组件
 * 只负责渲染具体的工具（分镜表、角色生成器等），不再负责渲染 Sidebar
 */
const WorkspaceContent = () => {
  const { userId } = useUser();
  const { projectId, episodeId, tab } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { currentStyle } = useStyle();
  const { getTheme } = useTheme();
  const theme = getTheme();

  const activeTab = tab || "mini-storyboard";
  const [storyboardData, setStoryboardData] = useState(null);
  const [storyboardLoading, setStoryboardLoading] = useState(false);
  const [originalText, setOriginalText] = useState("");
  const [workstationImage, setWorkstationImage] = useState(null);
  const [, setSaveBoard] = useState(true);

  // 资产确认弹窗状态
  const [showAssetConfirm, setShowAssetConfirm] = useState(false);
  const [assetConfirmData, setAssetConfirmData] = useState(null);

  // Lazy tab rendering: only mount tabs when first visited, keep mounted after
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([activeTab]));

  useEffect(() => {
    setVisitedTabs(prev => {
      if (prev.has(activeTab)) return prev;
      const next = new Set(prev);
      next.add(activeTab);
      return next;
    });
  }, [activeTab]);

  // 加载数据
  useEffect(() => {
    const loadData = async () => {
      if (!userId || !episodeId) return;
      setStoryboardLoading(true);
      try {
        const data = await authenticatedFetch(
          `${API_HOST}/api/storyboard/load?episodeId=${episodeId}`,
          { headers: { "X-User-ID": userId } }
        );
        // 确保始终返回数组，即使是空的
        const formattedData = Array.isArray(data) ? data.map((item) => ({
          ...item,
          sketch_preview: item.sketch_base64 || null,
        })) : [];
        setStoryboardData(formattedData);
      } catch (e) { console.error("Load failed", e); }
      finally { setStoryboardLoading(false); }
    };
    loadData();
  }, [userId, episodeId]);

  const handleUploadSuccess = useCallback(async (text, style, customPrompt, setLoadingStage) => {
      // 导入 JSON 模式：数据已在后端保存，直接刷新页面
      if (style === 'import') {
        toast.success("分镜数据导入成功！");
        navigate(`/project/${projectId}/episode/${episodeId}/storyboard`, { replace: true });
        const loadRes = await authenticatedFetch(`${API_HOST}/api/storyboard/load?episodeId=${episodeId}`);
        const loadData = typeof loadRes === 'string' ? JSON.parse(loadRes) : loadRes;
        const formattedData = Array.isArray(loadData) ? loadData.map((item) => ({
          ...item,
          sketch_preview: item.sketch_base64 || null,
        })) : [];
        setStoryboardData(formattedData);
        return;
      }

      // 其他模式：调用 AI 生成
      try {
        setOriginalText(text);

        const isTwoStage = style === 'two-stage';
        const isUltimate = style === 'ultimate';
        const isUltimateAssets = style === 'ultimate-assets';
        const isWithAssets = style === 'with-assets' || isTwoStage || isUltimate || isUltimateAssets;
        const isWithFirstLastFrame = style === 'with-first-last-frame';
        const isNineGrid = style === 'nine-grid';
        const isNovel = style === 'novel';
        const isSeedance = style === 'seedance';
        const isExplainerComic = style === 'explainer-comic';
        const isExplainerComicAssets = style === 'explainer-comic-assets';
        const isMainShotDirector = style === 'main-shot-director';

        // novel / with-assets 走两阶段流程（先提取，再确认，再生图）
        const needsConfirm = isNovel || isWithAssets;

        const taskType = isWithFirstLastFrame
          ? "CREATE_STORYBOARD_WITH_FIRST_LAST_FRAME"
          : (isExplainerComicAssets ? "CREATE_STORYBOARD_WITH_ASSETS"
            : (needsConfirm ? "EXTRACT_ASSETS_ONLY"
              : (isNineGrid ? "CREATE_NINE_GRID_STORYBOARD" : "CREATE_STORYBOARD")));

        const taskPayload = {
          script: text,
          style: (isWithAssets || isWithFirstLastFrame || isNineGrid) ? 'default' : style,
          prompt: customPrompt,
        };

        if (isWithAssets || isWithFirstLastFrame || isNineGrid || isNovel || isExplainerComicAssets || isMainShotDirector) {
          taskPayload.projectId = projectId;
          taskPayload.episodeId = episodeId;
          if (isNovel) taskPayload.style = 'novel';
          if (isExplainerComicAssets) taskPayload.style = 'explainer-comic-assets';
          if (isUltimate) taskPayload.style = 'ultimate';
          if (isUltimateAssets) taskPayload.style = 'ultimate-assets';
        }

        if (isSeedance) taskPayload.style = 'seedance';
        if (isExplainerComic) taskPayload.style = 'explainer-comic';

        toast.info(needsConfirm
          ? "AI正在分析剧本并提取角色/场景/道具，请稍候..."
          : (isWithFirstLastFrame ? "AI正在分析剧本并生成角色/场景/道具图片，请耐心等待..."
            : (isExplainerComicAssets ? "解说漫剧本解析中，提取资产并生成分镜..."
              : (isNineGrid ? "AI正在生成9宫格提示词节点，请稍候..."
                : (isSeedance ? "AI正在生成视频分镜提示词，请稍候..."
                  : (isExplainerComic ? "解说漫剧本解析中，跳过编剧直接进入分镜师..." : "分镜生成已提交，请稍候..."))))));

        const activeProvider = localStorage.getItem("ai_provider") || "omini";
        const { taskId } = await submitTask(taskType, taskPayload, { styleAnchor: currentStyle, provider: activeProvider });

        // 开始轮询，并更新加载阶段
        startTaskPolling(taskId, {
          onProgress: (progressData) => {
            // 已知阶段走 CoolUploader 专属文案；未知阶段透传后端原文（currentStageText 优先）
            setLoadingStage?.(resolveLoadingStageFromProgress(progressData, 'scriptwriter'));
          },
          onSuccess: async (result) => {
            setLoadingStage?.('finalizing');

            // ===== 两阶段流程：提取完成，弹出确认弹窗 =====
            if (needsConfirm) {
              let resultObj;
              try {
                resultObj = normalizeTaskResultObject(result);
              } catch {
                toast.error("返回数据解析失败");
                setLoadingStage?.('');
                return;
              }

              // 暂停 loading，弹出确认弹窗
              setLoadingStage?.('');
              setAssetConfirmData({
                storyboard: resultObj.storyboard,
                extractedAssets: resultObj.extractedAssets || { characters: [], scenes: [], props: [] },
                existingAssets: resultObj.existingAssets || { characters: [], scenes: [], props: [] },
                episodeId,
                projectId,
                setLoadingStage,
              });
              setShowAssetConfirm(true);
              return;
            }

            // ===== 其他模式（首尾帧、9宫格、解说漫+资产等）原有逻辑 =====
            if (isWithFirstLastFrame || isNineGrid || isExplainerComicAssets) {
              let resultObj;
              try {
                resultObj = normalizeTaskResultObject(result);
              } catch {
                toast.error("返回数据解析失败");
                setLoadingStage?.('');
                return;
              }

              if (isNineGrid) {
                if (!resultObj.nine_grid_prompt) { toast.error("9宫格数据缺少 nine_grid_prompt 字段"); setLoadingStage?.(''); return; }
                if (!resultObj.four_grid_prompts || !Array.isArray(resultObj.four_grid_prompts)) { toast.error("9宫格数据缺少 four_grid_prompts 字段"); setLoadingStage?.(''); return; }
                toast.success("9宫格提示词生成完成！已保存到画布");
                setLoadingStage?.('');
                setTimeout(() => navigate(`/project/${projectId}/episode/${episodeId}/canvas`), 800);
                return;
              }

              let storyboardParsed;
              try {
                storyboardParsed = extractStoryboardRows(resultObj);
                if (!storyboardParsed) throw new Error("missing storyboard rows");
              } catch {
                toast.error("分镜数据解析失败");
                setLoadingStage?.('');
                return;
              }

              const savedData = await handleSaveScript(storyboardParsed, setSaveBoard);
              if (savedData && Array.isArray(savedData)) setStoryboardData(savedData);
              else setStoryboardData(storyboardParsed);
              navigate(`/project/${projectId}/episode/${episodeId}/storyboard`);
              const assetIds = resultObj.assets || {};
              toast.success(
                `生成完成！分镜 ${Array.isArray(storyboardParsed) ? storyboardParsed.length : 0} 个，` +
                `角色 ${assetIds.characterIds?.length || 0} 个，场景 ${assetIds.sceneIds?.length || 0} 个，道具 ${assetIds.propIds?.length || 0} 个`
              );
              setLoadingStage?.('');
            } else {
              let data;
              try {
                data = extractStoryboardRows(result);
                if (!data) throw new Error("missing storyboard rows");
              } catch {
                toast.error("分镜数据解析失败，请检查 AI 返回格式");
                setLoadingStage?.('');
                return;
              }
              const savedData = await handleSaveScript(data, setSaveBoard);
              setStoryboardData(Array.isArray(savedData) ? savedData : data);
              navigate(`/project/${projectId}/episode/${episodeId}/storyboard`);
              toast.success("分镜生成成功！");
              setLoadingStage?.('');
            }
          },
          onError: (errorMessage) => {
            console.error("[分镜生成] 失败:", errorMessage);
            setLoadingStage?.('error:' + (errorMessage || '未知错误'));
          },
        });
      } catch (error) {
        console.error("[分镜生成] 提交失败:", error);
        setLoadingStage?.('error:提交失败 - ' + (error.message || '未知错误'));
      }
    }, [userId, episodeId, navigate, projectId, toast, setStoryboardData, currentStyle]);

  // 用户在资产确认弹窗点击"确认，开始生图"
  const handleAssetConfirm = useCallback(async (confirmedAssets) => {
    if (!assetConfirmData) return;
    const { storyboard, episodeId: eid, projectId: pid, setLoadingStage } = assetConfirmData;
    setShowAssetConfirm(false);
    setAssetConfirmData(null);
    setLoadingStage?.('director');

    try {
      const { taskId } = await submitTask("GENERATE_WITH_CONFIRMED_ASSETS", {
        storyboard,
        episodeId: eid,
        projectId: pid,
        assets: confirmedAssets,
      }, { styleAnchor: currentStyle, provider: localStorage.getItem("ai_provider") || "omini" });

      startTaskPolling(taskId, {
        onProgress: (progressData) => {
          // 不再恒为 'director'：未知阶段透传后端原文（currentStageText 优先）
          setLoadingStage?.(resolveLoadingStageFromProgress(progressData, 'director'));
        },
        onSuccess: async (result) => {
          setLoadingStage?.('finalizing');
          let resultObj;
          try {
            resultObj = normalizeTaskResultObject(result);
          } catch {
            toast.error("返回数据解析失败");
            setLoadingStage?.('');
            return;
          }

          let storyboardParsed;
          try {
            storyboardParsed = extractStoryboardRows(resultObj);
            if (!storyboardParsed) throw new Error("missing storyboard rows");
          } catch {
            toast.error("分镜数据解析失败");
            setLoadingStage?.('');
            return;
          }

          const savedData = await handleSaveScript(storyboardParsed, setSaveBoard);
          if (savedData && Array.isArray(savedData)) setStoryboardData(savedData);
          else setStoryboardData(storyboardParsed);
          navigate(`/project/${pid}/episode/${eid}/storyboard`);
          const assetIds = resultObj.assets || {};
          toast.success(
            `生成完成！分镜 ${Array.isArray(storyboardParsed) ? storyboardParsed.length : 0} 个，` +
            `角色 ${assetIds.characterIds?.length || 0} 个，场景 ${assetIds.sceneIds?.length || 0} 个，道具 ${assetIds.propIds?.length || 0} 个`
          );
          setLoadingStage?.('');
        },
        onError: (errorMessage) => {
          console.error("[资产生图] 失败:", errorMessage);
          setLoadingStage?.('error:' + (errorMessage || '未知错误'));
        },
      });
    } catch (error) {
      console.error("[资产生图] 提交失败:", error);
      setLoadingStage?.('error:提交失败 - ' + (error.message || '未知错误'));
    }
  }, [assetConfirmData, currentStyle, navigate, toast]);

 const handleSaveScript = useCallback(async (dataToSave, setIsSaving) => {
    const data = dataToSave || storyboardData;
    if (!data || !episodeId) return null;
    if (!Array.isArray(data)) {
      console.error("[分镜保存] 数据不是数组:", data);
      toast.error("保存失败：分镜数据不是数组");
      return null;
    }

    // 1. 安全包装 setIsSaving，防止未传入时报错
    const safeSetIsSaving = (val) => {
      if (typeof setIsSaving === 'function') {
        setIsSaving(val);
      }
    };

    safeSetIsSaving(true);

    try {
      // 剥离 base64 数据，大幅减少请求体积
      // base64 图片应该在生成时就已上传为 URL，保存时不应再传 base64
      const cleanData = data.map(shot => {
        const clean = { ...shot };
        // 如果是 base64 就置空，已经是 URL 的保留
        const stripBase64 = (val) => (val && val.startsWith('data:')) ? null : val;
        clean.generated_image = stripBase64(clean.generated_image);
        clean.sketch_base64 = stripBase64(clean.sketch_base64);
        clean.sketch_preview = stripBase64(clean.sketch_preview);
        if (clean.image_candidates && Array.isArray(clean.image_candidates)) {
          clean.image_candidates = clean.image_candidates.map(c => (c && c.startsWith('data:')) ? null : c).filter(Boolean);
        }
        if (clean.history_list && Array.isArray(clean.history_list)) {
          clean.history_list = clean.history_list.map(h => ({
            ...h,
            url: (h.url && h.url.startsWith('data:')) ? null : h.url
          })).filter(h => h.url);
        }
        return clean;
      });

      const savedData = await authenticatedFetch(
        `${API_HOST}/api/storyboard/save?episodeId=${episodeId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-User-ID": userId
          },
          body: JSON.stringify(cleanData),
          timeout: 600000
        }
      );
      return savedData;
    } catch (error) {
      console.error("Save error:", error);
      toast.error("保存失败: " + error.message);
      return null;
    } finally {
      safeSetIsSaving(false);
    }
  }, [storyboardData, episodeId, userId, toast]); // 依赖项

  const handleWorkstationSave = () => {
    setWorkstationImage(null);
    toast.success("图片已更新！请手动下载或在对应模块重新上传使用。");
  };

  return (
    <>
      {/* 资产确认弹窗 */}
      {showAssetConfirm && assetConfirmData && (
        <AssetConfirmModal
          extractedAssets={assetConfirmData.extractedAssets}
          existingAssets={assetConfirmData.existingAssets}
          onConfirm={handleAssetConfirm}
          onCancel={() => {
            setShowAssetConfirm(false);
            setAssetConfirmData(null);
          }}
        />
      )}
      <div className="w-full h-full relative overflow-hidden">
          {visitedTabs.has("upload") && (
            <div className={activeTab === "upload" ? "w-full h-full overflow-y-auto" : "hidden"}><div className="w-full min-h-full flex items-center justify-center p-10"><CoolUploader onSuccess={handleUploadSuccess} projectId={projectId} episodeId={episodeId} hasExistingStoryboard={storyboardData && Array.isArray(storyboardData) && storyboardData.length > 0} /></div></div>
          )}
          {visitedTabs.has("character") && (
            <div className={activeTab === "character" ? "w-full h-full overflow-hidden" : "hidden"}><CharacterGenerator onOpenWorkstation={setWorkstationImage} projectId={projectId} /></div>
          )}
          {visitedTabs.has("scene") && (
            <div className={activeTab === "scene" ? "w-full h-full overflow-hidden" : "hidden"}><SceneGenerator onOpenWorkstation={setWorkstationImage} projectId={projectId} /></div>
          )}
          {visitedTabs.has("prop") && (
            <div className={activeTab === "prop" ? "w-full h-full overflow-hidden" : "hidden"}><PropGenerator onOpenWorkstation={setWorkstationImage} projectId={projectId} /></div>
          )}
          {visitedTabs.has("orbit") && (
            <div className={activeTab === "orbit" ? "w-full h-full overflow-hidden" : "hidden"}><Orbit3DPage /></div>
          )}
          {visitedTabs.has("resources") && (
            <div className={activeTab === "resources" ? "w-full h-full overflow-hidden" : "hidden"}><ResourceCenter projectId={projectId} onOpenWorkstation={setWorkstationImage} /></div>
          )}
          {visitedTabs.has("imageagent") && (
            <div className={activeTab === "imageagent" ? "w-full h-full overflow-hidden" : "hidden"}>
                <ImageAgentModern projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("storyboard") && (
            <div className={activeTab === "storyboard" ? "w-full h-full overflow-hidden" : "hidden"}>
                {storyboardLoading ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
                          style={{ borderColor: theme.colors.accentPrimary, borderTopColor: 'transparent' }}></div>
                        <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>加载分镜中...</span>
                      </div>
                    </div>
                ) : Array.isArray(storyboardData) && storyboardData.length > 0 ? (
                    <StoryboardTable
                      data={storyboardData}
                      originalText={originalText}
                      onDataChange={setStoryboardData}
                      projectId={projectId}
                      episodeId={episodeId}
                      onSave={handleSaveScript}
                      onOpenWorkstation={(imgUrl) => setWorkstationImage(imgUrl)}/>
                ) : (
                    <EmptyState
                      onGoToUpload={() => navigate(`/project/${projectId}/episode/${episodeId}/upload`)}
                      onCreateShot={async () => {
                        const emptyShot = [{
                          shot_number: 1,
                          content: "",
                          visual_content: "",
                          audio: "",
                          audio_desc: "",
                          action: "",
                          shot_type: "全景",
                          generated_image: null,
                          generated_video: null,
                          ref_character_ids: [],
                          ref_scene_ids: [],
                          ref_prop_ids: [],
                          continuity: null,
                          openai_generation: null,
                        }];
                        await handleSaveScript(emptyShot);
                        setStoryboardData(emptyShot);
                      }}
                    />
                )}
            </div>
          )}
          {visitedTabs.has("master-shot") && (
            <div className={activeTab === "master-shot" ? "w-full h-full overflow-hidden" : "hidden"}>
              <MasterShotStoryboardPage projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("storyboard-board") && (
            <div className={activeTab === "storyboard-board" ? "w-full h-full overflow-hidden" : "hidden"}>
              <EpisodeStoryboardBoardWorkbench projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("mini-storyboard") && (
            <div className={activeTab === "mini-storyboard" ? "w-full h-full overflow-hidden" : "hidden"}>
              <MiniStoryboardWorkbench key={`${projectId || "project"}:${episodeId || "episode"}:mini-storyboard`} projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("canvas") && (
            <div className={activeTab === "canvas" ? "w-full h-full overflow-hidden" : "hidden"}>
                <CanvasMode projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("videoagent") && (
            <div className={activeTab === "videoagent" ? "w-full h-full overflow-hidden" : "hidden"}>
                <VideoAgentModern projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("musicagent") && (
            <div className={activeTab === "musicagent" ? "w-full h-full overflow-hidden" : "hidden"}>
                <MusicAgentModern projectId={projectId} episodeId={episodeId} />
            </div>
          )}
          {visitedTabs.has("education") && (
            <div className={activeTab === "education" ? "w-full h-full overflow-hidden" : "hidden"}>
                <EducationCenter />
            </div>
          )}
          {visitedTabs.has("motion-storyboard") && (
            <div className={activeTab === "motion-storyboard" ? "w-full h-full overflow-hidden" : "hidden"}>
                <MotionStoryboardPage projectId={projectId} episodeId={episodeId} />
            </div>
          )}
      </div>

      {workstationImage && (
          <ImageWorkstation
            imageUrl={workstationImage}
            onClose={() => setWorkstationImage(null)}
            onSave={handleWorkstationSave}
            leftOffset="0px"
            projectId={projectId}
            episodeId={episodeId}
          />
      )}
    </>
  );
};

const NavButton = React.memo(({ active, onClick, icon, label, collapsed }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  return (
  <button
    onClick={(event) => {
      onClick?.(event);
      window.dispatchEvent(new CustomEvent("app-sidebar-close"));
    }}
    title={collapsed ? label : ""}
    aria-current={active ? "page" : undefined}
    className={`flex items-center transition-all duration-200 group relative cursor-pointer ${collapsed ? "justify-center w-10 h-10 mx-auto" : "h-10 gap-3 px-3 w-full"}`}
    style={{
      borderRadius: 'var(--radius-md)',
      background: active ? 'var(--color-accent-light)' : 'transparent',
      border: '1px solid transparent',
      color: active ? theme.colors.accentSecondary : 'var(--color-text-muted)',
    }}
    onMouseEnter={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; e.currentTarget.style.borderColor = 'var(--color-border-primary)'; }}}
    onMouseLeave={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}}>
    {active && !collapsed && (
      <span
        className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full"
        style={{ backgroundColor: theme.colors.accentPrimary }}
        aria-hidden="true"
      />
    )}
    <span className="flex-shrink-0 transition-all duration-200"
      style={{ color: active ? theme.colors.accentPrimary : 'var(--color-text-muted)' }}
    >{icon}</span>
    <span className={`font-semibold whitespace-nowrap overflow-hidden transition-all duration-300 ${collapsed ? "w-0 opacity-0" : "w-auto opacity-100"} text-sm`} style={{ color: active ? theme.colors.accentPrimary : 'var(--color-text-secondary)' }}>{label}</span>
  </button>
)});


const EmptyState = ({ onGoToUpload, onCreateShot }) => (
  <div className="w-full h-full flex items-center justify-center">
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="p-4 rounded-2xl mb-4" style={{ backgroundColor: 'var(--color-accent-light)' }}>
        <Clapperboard size={32} style={{ color: 'var(--color-accent-primary)' }} />
      </div>
      <h3 className="text-sm font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>本集暂无分镜</h3>
      <p className="text-xs max-w-xs leading-relaxed mb-4" style={{ color: 'var(--color-text-muted)' }}>
        分镜应从剧本中心保存的本集剧本生成。先导入本集剧本再拆分，后续大故事板和小故事板才能追溯同一版内容。
      </p>
      <div className="flex items-center gap-3">
        <button onClick={onGoToUpload} className="px-5 py-2.5 rounded-lg text-xs font-bold text-[var(--color-accent-contrast)] transition-all duration-200 cursor-pointer hover:scale-[1.02] active:scale-[0.98]" style={{ backgroundColor: 'var(--color-accent-primary)', boxShadow: '0 2px 8px var(--color-accent-glow)' }}
          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 16px var(--color-accent-glow)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 2px 8px var(--color-accent-glow)'; }}>
          导入本集剧本
        </button>
        <button onClick={onCreateShot} className="px-5 py-2.5 rounded-lg text-xs font-bold transition-all duration-200 cursor-pointer hover:scale-[1.02] active:scale-[0.98] border" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', borderColor: 'var(--color-border-primary)' }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-hover)'; e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--color-border-primary)'; e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; }}>
          直接创建分镜
        </button>
      </div>
    </div>
  </div>
);

export default App;
